// active.jsx — the live workout screen.
//
// The session owns its exercise list (session_model.js), so everything here is
// editable mid-workout: add / swap / reorder / remove exercises, warm-up, drop
// and failure sets, supersets, per-exercise rest. One "focus" row is the next
// thing to do. Rest state lives on the session so it survives leaving the
// screen (WorkoutMiniBar + RestWatcher in this file keep it alive app-wide).

import React from 'react';
import { LLSheet } from './sheets.jsx';
import { fmtWeight, unitLabel, useApp } from './store.jsx';
import { fireRestFlash, fireRestNotification, playRestBeep, requestNotificationPermission, tryVibrate, unlockAudioContext } from './timers.jsx';
import { LL, LLIcon, LLSwitchTrack } from './tokens.jsx';
import { ExercisePicker, useExerciseIndex, resolveTarget, rankSubstitutes, itemToExercise } from './exercise_picker.jsx';
import { MuscleThumb } from './musclemap.jsx';
import { resolvePrefill, adaptiveRestSeconds, localDateStr } from './utils.jsx';
import {
  sessionSlots, slotFromExercise, setsForSlot, nextRow, focusOf, restAfter, warmupRamp,
  platesFor, stepFor, workIndexOf, structureChanged, slotId, SET_TYPES, isStaleSession, staleEndAt,
  TECHNIQUES, isTechnique, isMainRow, miniOwner, miniNumber, miniRows, minisAfter, slotKind,
} from './session_model.js';
import {
  e1rm, e1rmRPE, loadFor, readiness, exerciseKind, isBwLoad, effectiveLoad, toLoggedLoad, bodyweightAt, setLoad,
  loadText, effortText, rirFromRpe, fmtDuration, fmtDistance, fmtPace, paceSecPerKm, distUnit, metersFrom,
  metersTo, fmtHold, setText as fmtSetText, setsVolume, feedbackDue, sessionMuscles, jointFlagFor, FEEDBACK_OPTIONS, musclesFor, completedSessions, hardSetValue,
} from './analytics.js';
import { catalogEntryFor, useCatalog } from './exercise_catalog.js';
import { parseQuickLog, toLb } from './quicklog.js';
import { useSimple, HowDidItFeel, Explain } from './explain.jsx';
import { plainEffort, plainEffortShort } from './glossary.js';
import { resolveExercise } from './coach_tools.js';
import { aiEnabled, completeJSON } from './ai.js';
import { pushRestAlert, cancelRestAlert } from './sync.js';
import { useWatchLink } from './native.js';
import { planSuggestion, roundTo } from './progression.js';
import { useSheetHistory } from './sheet_history.js';
import { historyOf } from './history.js';
import { FormSheet, demoFor } from './video_player.jsx';

const KG = 0.45359237;
const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function fmtClock(s) {
  s = Math.max(0, Math.floor(s));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}
// Cardio time entry works like a microwave: digits fill from the right
// ("2","5","3","0" → 25:30; five or more digits → h:mm:ss).
const durDigits = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return (h ? `${h}${String(m).padStart(2, '0')}` : String(m)) + String(sec).padStart(2, '0');
};
const digitsSecs = (d) => {
  d = String(d || '').replace(/\D/g, '');
  if (!d) return 0;
  const sec = +d.slice(-2), rest = d.slice(0, -2);
  if (rest.length > 2) return +rest.slice(0, -2) * 3600 + +rest.slice(-2) * 60 + sec;
  return (+rest || 0) * 60 + sec;
};
const fmtDigits = (d) => {
  d = String(d || '').replace(/\D/g, '');
  if (!d) return '';
  const p = d.padStart(3, '0');
  const sec = p.slice(-2), rest = p.slice(0, -2);
  if (rest.length > 2) return `${+rest.slice(0, -2)}:${rest.slice(-2)}:${sec}`;
  return `${+rest}:${sec}`;
};

// Body weight and totals that include it read as whole numbers ("BW 178 + 30 = 208 lb").
const fmtWhole = (lb, units) => String(Math.round(units === 'kg' ? lb * KG : lb));
const fmtK = (lb, units) => {
  const v = units === 'kg' ? lb * KG : lb;
  return v >= 10000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v).toLocaleString();
};
// Header volume: always short ("1.1k") so it never truncates at 390px.
const fmtShortK = (lb, units) => {
  const v = units === 'kg' ? lb * KG : lb;
  return v >= 1000 ? `${(v / 1000).toFixed(v >= 100000 ? 0 : 1)}k` : String(Math.round(v));
};
// Plate maths only makes sense for loadable bars.
const usesPlates = (equipment) => /barbell|smith|ez|trap|olympic/i.test(equipment || '');
// Bodyweight / timed movements legitimately log 0; everything else needs a load.
const isBodyweight = (slot, equipment, kind) => slot.unit === 'sec' || /body|none/i.test(equipment || '') || !!(kind && isBwLoad(kind.load));
// Hard caps against fat-finger entries (stored in lb).
const MAX_LB = 1500;
const MAX_REPS = 100;
// Status colours as text: pulled toward the foreground so they stay legible on
// light surfaces as well as dark ones.
const ink = (c) => `color-mix(in oklab, ${c} 68%, ${LL.fg})`;
const TECH_COLOR = { myo: LL.info, restpause: LL.info, cluster: LL.info };

// Inject keyframes once.
function useActiveStyles() {
  React.useEffect(() => {
    if (document.getElementById('ll-active-style')) return;
    const el = document.createElement('style');
    el.id = 'll-active-style';
    el.textContent = `
      @keyframes llCheckPop { 0% { transform: scale(.55); } 60% { transform: scale(1.12); } 100% { transform: scale(1); } }
      @keyframes llRowFlash { 0% { background-color: var(--ll-flash-from); } 100% { background-color: transparent; } }
      @keyframes llSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      @keyframes llPulse { 0%,100% { opacity: 1; } 50% { opacity: .55; } }
      @keyframes llPrIn { 0% { transform: scale(.6); opacity: 0; } 70% { transform: scale(1.1); opacity: 1; } 100% { transform: scale(1); } }
      @keyframes llFadeUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
      @media (prefers-reduced-motion: reduce) { .ll-anim { animation: none !important; transition: none !important; } }
    `;
    document.head.appendChild(el);
  }, []);
}

// ——— Shared data hooks ————————————————————————————————————————————

// Last completed session's main sets per exercise, in order:
// { [exerciseId]: [{ w, reps, rpe, bw, dist, dur, date }] }. Warm-ups and
// myo / rest-pause / cluster mini-sets are left out so rows line up.
function useLastPerf(sessions, excludeId) {
  return React.useMemo(() => {
    const out = {};
    const done = (sessions || []).filter(s => s.completedAt && s.id !== excludeId).sort((a, b) => b.startedAt - a.startedAt);
    for (const s of done) {
      const byEx = {};
      for (const st of s.sets || []) {
        if (st.type === 'warmup' || st.type === 'mini') continue;
        if (!(st.reps > 0) && !(st.dist > 0) && !(st.dur > 0)) continue;
        (byEx[st.exerciseId] = byEx[st.exerciseId] || []).push(st);
      }
      for (const [ex, sets] of Object.entries(byEx)) {
        if (out[ex]) continue;
        // Prefer the newest session that used real weight (a stray 0-weight quick log shouldn't shadow history).
        if (sets.every(x => !(x.w > 0) && !(x.dist > 0) && !(x.dur > 0)) && done.some(o => o !== s && o.startedAt < s.startedAt && (o.sets || []).some(x => x.exerciseId === ex && x.w > 0))) continue;
        out[ex] = sets.sort((a, b) => a.setIdx - b.setIdx).map(x => ({ w: x.w, reps: x.reps, rpe: x.rpe ?? null, bw: x.bw || null, dist: x.dist || null, dur: x.dur || null, date: s.startedAt }));
      }
    }
    return out;
  }, [sessions, excludeId]);
}

// Last completed session's main sets per program slot (peId), so a lift that
// appears twice in a day (top sets, then back-offs) lines up with its own rows.
function useLastPerfByPe(sessions, excludeId) {
  return React.useMemo(() => {
    const out = {};
    const done = (sessions || []).filter(s => s.completedAt && s.id !== excludeId).sort((a, b) => b.startedAt - a.startedAt);
    for (const s of done) {
      const byPe = {};
      for (const st of s.sets || []) {
        if (!st.peId || st.type === 'warmup' || st.type === 'mini') continue;
        if (!(st.reps > 0) && !(st.dist > 0) && !(st.dur > 0)) continue;
        (byPe[st.peId] = byPe[st.peId] || []).push(st);
      }
      for (const [pe, sets] of Object.entries(byPe)) {
        if (out[pe]) continue;
        out[pe] = { exerciseId: sets[0].exerciseId, sets: sets.sort((a, b) => a.setIdx - b.setIdx).map(x => ({ w: x.w, reps: x.reps, rpe: x.rpe ?? null, bw: x.bw || null, dist: x.dist || null, dur: x.dur || null, date: s.startedAt })) };
      }
    }
    return out;
  }, [sessions, excludeId]);
}

// Best e1RM per exercise from completed sessions, on effective load (body
// weight ± logged load for bodyweight lifts). Timed and cardio work never
// produces an e1RM; mini-sets don't count.
function useBestE1RM(state, excludeId, kindOfEx) {
  // Completed history only (stable while sets are logged — history.js).
  const hist = historyOf(state.sessions);
  return React.useMemo(() => {
    const best = {};
    for (const s of hist) {
      if (!s.completedAt || s.id === excludeId) continue;
      for (const st of s.sets || []) {
        if (st.type === 'warmup' || st.type === 'mini' || !(st.reps > 0)) continue;
        const kind = kindOfEx(st.exerciseId);
        if (kind.track !== 'reps') continue;
        const eff = setLoad(state, st, kind, s.startedAt);
        if (!(eff > 0)) continue;
        const v = e1rm(eff, st.reps);
        if (!best[st.exerciseId] || v > best[st.exerciseId]) best[st.exerciseId] = v;
      }
    }
    return best;
  }, [hist, state.bodyEntries, excludeId, kindOfEx]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Suggested { w, reps } (or { dist, dur } for cardio) for a row.
function suggestionFor(args) {
  const s = rawSuggestion(args);
  // Deload week (−10%) and the "lighter day" check-in (−5%) scale working loads.
  // Bodyweight lifts are lightened by the dropped set instead (assistance would invert).
  const scale = args.session.deload ? 0.9 : args.session.lighter ? 0.95 : 1;
  if (scale === 1 || !(s.w > 0) || s.tier === 'warmup' || s.cardio || (args.kind && isBwLoad(args.kind.load))) return s;
  const step = stepFor(args.slot, args.units);
  return { ...s, w: Math.max(0, Math.round((s.w * scale) / step) * step) };
}

function rawSuggestion(args) {
  const { slot, row, session, lastPerf, mode, units, equipment, kind, bw } = args;
  const type = slot.rows[row]?.type || 'normal';
  const step = stepFor(slot, units);
  const pe = { ...slot, incLb: step };
  const logged = setsForSlot(session, slot);
  const bwKind = !!(kind && isBwLoad(kind.load));

  // Cardio: repeat the previous interval of this workout, else last time's.
  if (kind?.track === 'distance-time') {
    const wi = Math.max(0, workIndexOf(slot, row));
    const same = logged.filter(st => st.setIdx < row && (st.dist > 0 || st.dur > 0)).sort((a, b) => b.setIdx - a.setIdx)[0];
    const lp = lastPerf && lastPerf[slot.exerciseId];
    const prev = same || (lp ? (lp[wi] || lp[lp.length - 1]) : null);
    return { w: 0, reps: 0, dist: prev?.dist || null, dur: prev?.dur || null, tier: prev ? 'last' : 'no-history', cardio: true };
  }

  // Mini-set: the activation set's load; reps by technique.
  if (type === 'mini') {
    const own = miniOwner(slot, row);
    const tech = slot.rows[row].tech || 'myo';
    const prevMini = logged.filter(st => st.setIdx < row && st.setIdx > own && st.type === 'mini').sort((a, b) => b.setIdx - a.setIdx)[0];
    const ownerSet = logged.find(st => st.setIdx === own);
    const base = ownerSet || (own >= 0 ? rawSuggestion({ ...args, row: own }) : null);
    const act = base?.reps || slot.repMin || 8;
    const reps = prevMini ? prevMini.reps
      : tech === 'cluster' ? act
      : tech === 'restpause' ? Math.max(2, Math.round(act * 0.4))
      : Math.max(3, Math.min(5, Math.round(act / 4)));
    return { w: base?.w ?? null, reps, tier: 'mini' };
  }

  const working = (wi) => {
    // Percent / wave / RPE programs: the engine's plan for this row wins.
    const plan = planSuggestion({ slot, workIdx: wi, loggedSets: logged, units });
    if (plan) return plan;
    const sessionSets = logged.filter(st => st.type !== 'warmup' && st.type !== 'drop' && st.type !== 'mini')
      .map(st => ({ ...st, setIdx: workIndexOf(slot, st.setIdx) })).filter(st => st.setIdx >= 0);
    const r = resolvePrefill({
      pe, setIdx: wi, sessionSets,
      lastPerfFor: (exId, idx) => { const l = lastPerf[exId]; return l ? (l[idx] || l[l.length - 1]) : null; },
      mode,
    });
    let base = r || { w: slot.targetWeight || 0, reps: Math.round((slot.repMin + slot.repMax) / 2), tier: null };
    // History- and program-derived loads land on the user's plate grid (a kg
    // lifter never gets 61.2 kg). What was logged this session is mirrored as is.
    if (base.w > 0 && !bwKind && base.tier !== 'session') {
      base = { ...base, w: roundTo(base.w, units, step < (units === 'kg' ? 2.5 / KG : 5) - 1e-6) };
    }
    // No history and no programmed load on a weighted lift: show "—" and make
    // the user enter a weight instead of one-tap logging a 0 lb set.
    if (!(base.w > 0) && (!r || r.tier === 'no-history') && !(slot.targetWeight > 0) && !isBodyweight(slot, equipment, kind)) {
      base = { ...base, w: null };
    }
    // Autoregulation: if an earlier working set this session was rated well off
    // the target effort, re-derive the load from that set's RPE-adjusted e1RM
    // (RPE → %1RM table) so the next set lands back on target. Bodyweight lifts
    // work on the effective load (body weight ± added) and convert back.
    const rated = sessionSets.filter(st => st.setIdx < wi && st.rpe != null && st.reps > 0 && (st.w > 0 || bwKind))
      .sort((a, b) => b.setIdx - a.setIdx)[0];
    const targetRpe = slot.rpeTarget || 8;
    if (rated && Math.abs(rated.rpe - targetRpe) >= 1 && base.reps > 0 && base.reps <= 12 && base.w != null && (!bwKind || bw > 0)) {
      const effOf = (w) => (bwKind ? effectiveLoad(w, kind.load, bw) : w);
      const est = e1rmRPE(effOf(rated.w), rated.reps, rated.rpe);
      let eff = loadFor(est, base.reps, targetRpe, bwKind ? 0.5 : step);
      eff = Math.max(effOf(rated.w) * 0.85, Math.min(effOf(rated.w) * 1.1, eff));
      let w = bwKind ? toLoggedLoad(eff, kind.load, bw) : eff;
      w = Math.max(0, Math.round(w / step) * step);
      if (Math.abs(w - base.w) >= step * 0.99) {
        const heavier = bwKind && kind.load === 'assisted' ? w < base.w : w > base.w;
        return { ...base, w, tier: 'rpe', note: `Set ${rated.setIdx + 1} felt like RPE ${rated.rpe} (target ${targetRpe}). Load ${heavier ? 'raised' : 'lowered'} to keep you on target` };
      }
    }
    return base;
  };
  if (type === 'warmup') {
    const first = working(0);
    if (bwKind) {
      // Bodyweight warm-ups: easy reps at body weight (assisted keeps its help).
      return { w: kind.load === 'assisted' ? (first.w ?? 0) : 0, reps: Math.max(3, Math.round((slot.repMin || 8) / 2)), tier: 'warmup' };
    }
    if (first.w == null) return { w: null, reps: 8, tier: 'warmup' };
    const ramp = warmupRamp(first.w, { equipment, inc: step, bar: units === 'kg' ? 20 / KG : 45 });
    const k = slot.rows.slice(0, row).filter(r => r.type === 'warmup').length;
    const total = slot.rows.filter(r => r.type === 'warmup').length;
    const pick = ramp.length ? ramp[Math.max(0, ramp.length - total + k)] || ramp[k] : null;
    return { w: pick ? pick.w : Math.round(first.w * 0.5 / step) * step, reps: pick ? pick.reps : 8, tier: 'warmup' };
  }
  if (type === 'drop') {
    const prev = logged.find(st => st.setIdx === row - 1) || null;
    const base = prev ? prev.w : working(Math.max(0, workIndexOf(slot, row - 1))).w;
    if (base == null) return { w: null, reps: slot.repMin, tier: 'drop' };
    // Dropping load on a bodyweight lift: shed the added weight, or add assistance.
    if (bwKind) {
      const w = kind.load === 'assisted' ? Math.round((base + Math.max(step * 2, base * 0.3)) / step) * step : Math.max(0, Math.round((base * 0.5) / step) * step);
      return { w, reps: slot.repMin, tier: 'drop' };
    }
    return { w: Math.max(0, Math.round((base * 0.75) / step) * step), reps: slot.repMin, tier: 'drop' };
  }
  return working(Math.max(0, workIndexOf(slot, row)));
}

// ——— Active workout ————————————————————————————————————————————————

// retroDate ('YYYY-MM-DD'): "Log a past workout": the session starts on that
// day, runs without rest timers and asks for its duration at the end.
export function ActiveWorkout({ dayId, freestyle, retroDate }) {
  useActiveStyles();
  const { state, actions, replaceRoute, setRoute, toast } = useApp();
  const units = state.prefs.units;
  const activeProgram = state.programs.find(p => p.id === state.activeProgramId) || null;

  // Bootstrap: resume, start the requested day, or start a freestyle workout.
  const [conflict, setConflict] = React.useState(null);
  const booted = React.useRef(false);
  React.useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    unlockAudioContext();
    const live = state.sessions.find(s => s.id === state.activeSessionId && !s.completedAt);
    if (live) {
      // A session left open from an earlier day never silently absorbs today's sets.
      if (isStaleSession(live)) setConflict({ live, stale: true });
      else if (dayId && live.dayId !== dayId) setConflict({ live });
      return;
    }
    if (dayId && activeProgram && activeProgram.days.some(d => d.id === dayId)) {
      // Already finished this day today? Open its review instead of a phantom session.
      const today = new Date().toDateString();
      const doneToday = state.sessions.find(s => s.completedAt && s.dayId === dayId && new Date(s.startedAt).toDateString() === today);
      if (doneToday) { replaceRoute('workout-review', { sessionId: doneToday.id }); return; }
      actions.startSession(activeProgram.id, dayId);
    } else if (freestyle || retroDate) {
      const id = actions.startFreestyle(typeof freestyle === 'string' ? freestyle : 'Workout');
      const at = retroDate ? Date.parse(`${retroDate}T18:00:00`) : NaN;
      if (id && Number.isFinite(at) && at < Date.now()) actions.updateSession(id, { startedAt: at, retro: true });
    } else {
      replaceRoute('home'); // stale route with nothing to resume
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const session = state.sessions.find(s => s.id === state.activeSessionId && !s.completedAt) || null;
  const slots = React.useMemo(() => sessionSlots(session, state.programs), [session, state.programs]);
  // History-derived values key on the completed history, which keeps its
  // identity while sets are logged: logging a set never recomputes them.
  const history = historyOf(state.sessions);
  const lastPerf = useLastPerf(history, session?.id);
  const lastPerfPe = useLastPerfByPe(history, session?.id);
  // Lifters who rate effort get the RPE/RIR column from the first set (it never
  // pops in mid-exercise): a third of the working sets in their last 3 workouts.
  const ratesEffort = React.useMemo(() => {
    const recent = history.filter(s => s.completedAt && s.id !== session?.id).sort((a, b) => b.startedAt - a.startedAt).slice(0, 3);
    const work = recent.flatMap(s => (s.sets || []).filter(st => st.type !== 'warmup' && st.reps > 0));
    return work.length >= 4 && work.filter(st => st.rpe != null).length / work.length >= 0.33;
  }, [history, session?.id]);
  const exById = React.useMemo(() => Object.fromEntries(state.exercises.map(e => [e.id, e])), [state.exercises]);
  const catalog = useCatalog();
  // { load, track } per exercise id (catalog category fills in what the record lacks).
  const kindOfEx = React.useCallback((id) => exerciseKind(exById[id] || { id }, catalogEntryFor(id, catalog)), [exById, catalog]);
  const kindFor = React.useCallback((slot) => slotKind(slot, exById[slot.exerciseId], catalogEntryFor(slot.exerciseId, catalog)), [exById, catalog]);
  const bestE1 = useBestE1RM(state, session?.id, kindOfEx);
  const mode = state.prefs.progression?.mode ?? 'double';
  // Simple mode: effort reads as "reps left", per-muscle check-ins give way to
  // one "How did it feel?" at finish (explain.jsx).
  const { simple, setSimple } = useSimple();
  const effort = simple ? 'plain' : state.prefs.effortScale === 'rir' ? 'rir' : 'rpe';
  const bwNow = bodyweightAt(state, session?.retro ? session.startedAt : Date.now());
  const askFeedback = state.prefs.muscleFeedback !== false && !simple;
  // Muscles trained in the last three weeks: only those get "sore from last time?".
  const soreAsk = React.useMemo(() => {
    const out = new Set();
    const since = Date.now() - 21 * 86400000;
    for (const s of history) {
      if (!s.completedAt || s.startedAt < since) continue;
      for (const st of s.sets || []) if (st.type !== 'warmup') musclesFor(exById[st.exerciseId]).primary.forEach(m => out.add(m));
    }
    return out;
  }, [history, exById]);

  // Clock
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  // UI state
  const [typeSheet, setTypeSheet] = React.useState(null);   // { slotId, row }
  const [menuSlot, setMenuSlot] = React.useState(null);     // slotId
  const [picker, setPicker] = React.useState(null);         // { mode: 'add' | 'swap', slotId? }
  const [plateFor, setPlateFor] = React.useState(null);     // weight lb
  const [restPick, setRestPick] = React.useState(null);     // slotId
  const [noteSlot, setNoteSlot] = React.useState(null);     // slotId
  const [finishOpen, setFinishOpen] = React.useState(false);
  const [discardOpen, setDiscardOpen] = React.useState(false);
  const [video, setVideo] = React.useState(null);
  const [flash, setFlash] = React.useState(null);           // `${slotId}:${row}` just logged
  const [prRows, setPrRows] = React.useState({});           // `${slotId}:${row}` → true
  const lastLoggedRef = React.useRef(null);
  const scrollRef = React.useRef(null);
  const tapGuard = React.useRef({});
  const [quickOpen, setQuickOpen] = React.useState(false);
  const [typo, setTypo] = React.useState(null); // { slot, items: [{ row, payload }], usual }
  const [reorderOpen, setReorderOpen] = React.useState(false);
  const [pad, setPad] = React.useState(null);   // inline keypad: { slotId, row, field, wText, rText, ... }
  const watchLink = useWatchLink(); // Wear OS watch (native.js): filled below, after the actions exist

  if (conflict) {
    return (
      <ConflictScreen
        live={conflict.live}
        stale={!!conflict.stale}
        units={units}
        onResume={() => { setConflict(null); }}
        onSaveStale={() => {
          // Close it at its last set (not now) so history doesn't get a multi-day workout.
          actions.completeSession(conflict.live.id, { completedAt: staleEndAt(conflict.live) });
          setConflict(null);
          if (dayId && activeProgram && activeProgram.days.some(d => d.id === dayId)) actions.startSession(activeProgram.id, dayId);
          else if (freestyle) actions.startFreestyle(typeof freestyle === 'string' ? freestyle : 'Workout');
          else replaceRoute('workout-review', { sessionId: conflict.live.id });
        }}
        onDiscard={() => {
          actions.cancelSession(conflict.live.id, { silent: true });
          setConflict(null);
          if (dayId && activeProgram) actions.startSession(activeProgram.id, dayId);
          else if (freestyle) actions.startFreestyle(typeof freestyle === 'string' ? freestyle : 'Workout');
          else replaceRoute('home');
        }}
        onCancel={() => replaceRoute('home')}
      />
    );
  }

  if (!session) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center', justifyContent: 'center', background: LL.bg }}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgDim }}>Starting workout…</div>
        <button onClick={() => replaceRoute('home')} style={{ height: 40, padding: '0 16px', borderRadius: LL.r.md, border: `1px solid ${LL.line}`, background: 'transparent', color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.meta, cursor: 'pointer' }}>Back to home</button>
      </div>
    );
  }

  const elapsed = Math.floor((now - session.startedAt) / 1000);
  const volume = setsVolume(state, session.sets, session.startedAt);
  const totalRows = slots.reduce((a, s) => a + s.rows.length, 0);
  const doneRows = session.sets.length;
  const focus = focusOf(session, slots, lastLoggedRef.current);
  const exIndex = exById;

  // ——— actions ———
  // History for a slot: the same program slot first, else the exercise.
  const perfFor = (slot) => {
    const pe = lastPerfPe[slot.id];
    return pe && pe.exerciseId === slot.exerciseId ? pe.sets : lastPerf[slot.exerciseId];
  };
  const sugg = (slot, row) => suggestionFor({ slot, row, session, lastPerf: { [slot.exerciseId]: perfFor(slot) }, mode, units, equipment: exById[slot.exerciseId]?.equipment, kind: kindFor(slot), bw: bwNow });
  // A lift that appears twice today is told apart by what its rows prescribe.
  const dupIds = new Set(slots.map(s => s.exerciseId).filter((id, i, a) => a.indexOf(id) !== i));
  const dupTag = (slot) => {
    if (!dupIds.has(slot.exerciseId)) return null;
    const main = slot.rows.filter(isMainRow);
    if (!main.length) return null;
    const reps = [...new Set(main.map(r => r.reps).filter(x => x > 0))];
    const pcts = main.map(r => r.pct).filter(x => x > 0);
    const amrap = main.some(r => r.amrap);
    const rp = reps.length === 1 ? `${main.length}×${reps[0]}${amrap ? '+' : ''}` : reps.length ? `${Math.max(...reps)}s · top sets` : `${main.length}×${slot.repMin === slot.repMax ? slot.repMin : `${slot.repMin}–${slot.repMax}`}`;
    const pc = !pcts.length ? '' : Math.min(...pcts) === Math.max(...pcts) ? ` @ ${Math.round(pcts[0])}%` : ` · ${Math.round(Math.min(...pcts))}–${Math.round(Math.max(...pcts))}%`;
    if (!pcts.length && reps.length <= 1) return slots.filter(s => s.exerciseId === slot.exerciseId).indexOf(slot) === 0 ? 'first block' : 'second block';
    return rp + pc;
  };

  const startRest = (seconds, adapted, prescribed, label) => {
    if (!(seconds > 0) || !state.prefs.restAutoStart || session.retro) { actions.updateSession(session.id, { rest: null }); return; }
    actions.updateSession(session.id, { rest: { endsAt: Date.now() + seconds * 1000, total: seconds, adapted: !!adapted, prescribed: prescribed || seconds, fired: false, label: label || '' } });
  };

  // Typo guard: a weight far outside this lift's normal range is probably a slip
  // ("1350" for "135"). Without history, use the programmed load, else a sane
  // ceiling for the equipment.
  const typoFor = (slot, row, w) => {
    if (!(w > 0) || (slot.rows[row]?.type || 'normal') === 'warmup') return null;
    const kind = kindFor(slot);
    if (isBwLoad(kind.load)) {
      // Added load / assistance: judge the total moved, not the small added number.
      if (!(bwNow > 0)) return w > 250 ? { usual: 0 } : null;
      const hist = Math.max(0, ...(perfFor(slot) || []).map(p => effectiveLoad(p.w, kind.load, p.bw || bwNow)));
      const eff = effectiveLoad(w, kind.load, bwNow);
      if (kind.load === 'assisted' && w >= bwNow) return { usual: 0 };
      return hist > 0 && (eff > hist * 1.6 || eff < hist * 0.35) ? { usual: 0 } : (w > 250 ? { usual: 0 } : null);
    }
    const hist = Math.max(0, ...(perfFor(slot) || []).map(p => p.w || 0));
    const usual = hist > 0 ? hist : (slot.targetWeight > 0 ? slot.targetWeight : 0);
    if (usual > 0) return (w > usual * 1.6 || w < usual * 0.35) ? { usual } : null;
    const eq = exById[slot.exerciseId]?.equipment || '';
    const ceiling = /barbell|machine|smith|sled|trap|lever|cable/i.test(eq) ? 700 : 250;
    return w > ceiling ? { usual: 0 } : null;
  };
  const outOfRange = (w, reps) => {
    if (w > MAX_LB) { toast(`Over ${fmtWeight(MAX_LB, units)} ${unitLabel(units)} — check the weight`); return true; }
    if (reps > MAX_REPS) { toast(`Over ${MAX_REPS} reps — check the number`); return true; }
    return false;
  };
  // Bodyweight lifts snapshot today's body weight so history stays right when
  // it changes; mini-sets carry their technique; cardio carries distance + time.
  const setPayload = (slot, row, { w, reps, rpe, dist, dur }) => {
    const kind = kindFor(slot);
    const rowDef = slot.rows[row] || {};
    return {
      exerciseId: slot.exerciseId, peId: slot.id, setIdx: row, type: rowDef.type || 'normal',
      w, reps, rpe: rpe ?? null,
      ...(rowDef.type === 'mini' && rowDef.tech ? { tech: rowDef.tech } : {}),
      ...(isBwLoad(kind.load) && bwNow > 0 ? { bw: bwNow } : {}),
      ...(kind.track === 'distance-time' ? { dist: dist > 0 ? Math.round(dist) : 0, dur: dur > 0 ? Math.round(dur) : 0 } : {}),
      ...(slot.variant ? { performedName: slot.variant.name, performedYt: slot.variant.yt } : {}),
    };
  };

  // e1RM of a set on effective load (0 for warm-ups, mini-sets, timed and cardio work).
  const setE1 = (slot, st) => {
    const kind = kindFor(slot);
    if (kind.track !== 'reps' || st.type === 'warmup' || st.type === 'mini' || !(st.reps > 0)) return 0;
    const eff = isBwLoad(kind.load) ? effectiveLoad(st.w, kind.load, st.bw > 0 ? st.bw : bwNow) : (st.w || 0);
    return eff > 0 ? e1rm(eff, st.reps) : 0;
  };
  const rowLabel = (sl, row) => {
    const t = sl.rows[row]?.type;
    if (t === 'warmup') return 'warm-up';
    if (t === 'mini') return `mini-set ${miniNumber(sl, row)}`;
    return 'set ' + (workIndexOf(sl, row) + 1);
  };

  // After new rows are logged: PR check, rest timer, focus scroll / finish prompt.
  const afterLog = (slot, rows, payloads) => {
    // Live PR (working sets only) — one celebration per log action.
    const prior = bestE1[slot.exerciseId];
    const rowSet = new Set(rows);
    const sessionBest = Math.max(0, ...session.sets.filter(s => s.exerciseId === slot.exerciseId && !(s.peId === slot.id && rowSet.has(s.setIdx))).map(s => setE1(slot, s)));
    let best = null;
    payloads.forEach((pl, k) => {
      const est = setE1(slot, pl);
      if (est > 0 && (!best || est > best.est)) best = { est, row: rows[k] };
    });
    const lastRow = rows[rows.length - 1];
    lastLoggedRef.current = { peId: slot.id, row: lastRow };
    const nextSession = { ...session, sets: [...session.sets, ...rows.map(r => ({ peId: slot.id, setIdx: r }))] };
    const nf = focusOf(nextSession, slots, { peId: slot.id, row: lastRow });
    if (best && prior && best.est > prior + 0.01 && best.est > sessionBest) {
      setPrRows(p => ({ ...p, [`${slot.id}:${best.row}`]: true }));
      if (state.prefs.haptics) tryVibrate([20, 60, 30]);
      // The last set opens the finish sheet, and the summary celebrates PRs, so
      // no toast on top of it.
      if (nf) toast(`New PR · ${slot.name} est. 1RM ${fmtWeight(Math.round(best.est), units)} ${unitLabel(units)}`, 'pr');
    }

    if (!nf) {
      // Last planned set: no rest timer behind the finish sheet.
      actions.updateSession(session.id, { rest: null });
      setTimeout(() => setFinishOpen(true), 470);
      return;
    }
    // Rest — match the user's pace when they log before the timer ends. Sets
    // logged seconds apart (catching up, logging after the fact) aren't a pace signal.
    const prescribed = restAfter(session, slots, slot, lastRow, state.prefs);
    if (prescribed > 0) {
      const r = session.rest;
      const left = r && r.endsAt > Date.now() ? Math.ceil((r.endsAt - Date.now()) / 1000) : 0;
      const rested = r && left > 0 ? r.total - left : 0;
      const intra = slot.rows[lastRow + 1]?.type === 'mini';
      const secs = !intra && rows.length === 1 && left > 0 && rested >= 20 ? adaptiveRestSeconds(prescribed, r.total, left) : prescribed;
      const ns = slots.find(x => x.id === nf.slotId);
      const nLabel = ns ? `${ns.variant?.name || ns.name} · ${rowLabel(ns, nf.row)}` : '';
      startRest(secs, secs !== prescribed, prescribed, nLabel);
    } else {
      actions.updateSession(session.id, { rest: null });
    }
    // A muscle just finished: bring its one-tap check-in into view (it sits under
    // this exercise), else scroll the next focus into view when it moves on.
    const due = askFeedback ? feedbackDue(nextSession, slots, exIndex).filter(m => m.lastSlotId === slot.id) : [];
    if (due.length) {
      setTimeout(() => {
        const el = document.getElementById(`fb-${slot.id}`);
        const sc = scrollRef.current;
        if (el && sc) {
          // Whole card in view, clear of the rest bar (or as much as fits from its top).
          const r = el.getBoundingClientRect(), box = sc.getBoundingClientRect();
          const top = r.top - box.top, bottom = r.bottom - box.top, limit = box.height - 150;
          const by = bottom > limit ? Math.min(top - 8, bottom - limit) : top < 8 ? top - 8 : 0;
          if (by) sc.scrollBy({ top: by, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }, 140);
    } else if (nf.slotId !== slot.id) {
      setTimeout(() => {
        const el = document.getElementById(`slot-${nf.slotId}`);
        if (el && scrollRef.current) scrollRef.current.scrollTo({ top: el.offsetTop - 8, behavior: reduceMotion ? 'auto' : 'smooth' });
      }, 120);
    }
  };

  const isCardio = (slot) => kindFor(slot).track === 'distance-time';
  const validPayload = (slot, p) => (isCardio(slot)
    ? (p.dist > 0 || p.dur > 0)
    : p.reps > 0 && typeof p.w === 'number' && p.w >= 0);

  const commitSet = (slot, row, values, opts = {}) => {
    const cardio = isCardio(slot);
    const { w, reps, rpe } = cardio ? { w: 0, reps: 0, rpe: values.rpe } : values;
    if (!validPayload(slot, cardio ? values : { w, reps })) return;
    if (!cardio && outOfRange(w, reps)) return;
    if (!opts.confirmed && !cardio) {
      const t = typoFor(slot, row, w);
      if (t) { setTypo({ slot, items: [{ row, payload: { w, reps, rpe } }], usual: t.usual }); return; }
    }
    const payload = setPayload(slot, row, { w, reps, rpe, dist: values.dist, dur: values.dur });
    const isFirstLogOfRow = !setsForSlot(session, slot).some(s => s.setIdx === row);
    actions.logSet(session.id, payload);
    if (state.prefs.haptics) tryVibrate(12);
    const key = `${slot.id}:${row}`;
    setFlash(key);
    setTimeout(() => setFlash(f => (f === key ? null : f)), 450);
    if (!isFirstLogOfRow) {
      // Editing an already-logged set: no rest or focus jump, but a corrected
      // weight can still be a PR.
      const prior = bestE1[slot.exerciseId];
      const est = setE1(slot, payload);
      const others = Math.max(0, ...session.sets.filter(s => s.exerciseId === slot.exerciseId && !(s.peId === slot.id && s.setIdx === row)).map(s => setE1(slot, s)));
      if (prior && est > prior + 0.01 && est > others) setPrRows(p => ({ ...p, [key]: true }));
      return;
    }
    afterLog(slot, [row], [payload]);
  };

  // Several sets for one exercise in one commit (quick log). One typo prompt for
  // the whole batch, one toast with the real count.
  const commitBatch = (slot, items, opts = {}) => {
    const cardio = isCardio(slot);
    const valid = items.filter(it => validPayload(slot, it.payload));
    if (!valid.length) { toast(cardio ? 'Nothing to log: add a distance or time' : 'Nothing to log: add reps'); return; }
    if (!cardio && valid.some(it => outOfRange(it.payload.w, it.payload.reps))) return;
    const maxRow = Math.max(...valid.map(it => it.row));
    const need = Math.max(0, maxRow + 1 - slot.rows.length);
    const full = need ? { ...slot, rows: [...slot.rows, ...Array.from({ length: need }, () => ({ type: 'normal' }))] } : slot;
    if (!opts.confirmed && !cardio) {
      const hit = valid.map(it => typoFor(full, it.row, it.payload.w)).find(Boolean);
      if (hit) { setTypo({ slot, items: valid, usual: hit.usual }); return; }
    }
    if (need) editSlots(cur => cur.map(s => s.id !== slot.id ? s : { ...s, rows: [...s.rows, ...Array.from({ length: need }, () => ({ type: 'normal' }))] }));
    const norm = (p) => (cardio ? { ...p, w: 0, reps: 0 } : p);
    const payloads = valid.map(it => setPayload(full, it.row, norm(it.payload)));
    const fresh = valid.filter(it => !setsForSlot(session, slot).some(s => s.setIdx === it.row));
    actions.logSets(session.id, payloads);
    if (state.prefs.haptics) tryVibrate(12);
    toast(`Logged ${valid.length} ${cardio ? 'interval' : 'set'}${valid.length > 1 ? 's' : ''} · ${slot.name}`);
    if (fresh.length) afterLog(full, fresh.map(it => it.row), fresh.map(it => setPayload(full, it.row, norm(it.payload))));
  };

  // Undoable destructive edits.
  const deleteRow = (slotIdArg, row) => {
    const slot = slots.find(s => s.id === slotIdArg);
    if (!slot) return;
    if (slot.rows.length <= 1) { toast('Remove the exercise from its menu instead'); return; }
    const rowDef = slot.rows[row];
    const loggedSet = setsForSlot(session, slot).find(s => s.setIdx === row) || null;
    actions.deleteSessionRow(session.id, slot.id, row);
    toast('Set deleted', 'default', { action: { label: 'Undo', run: () => {
      actions.insertSessionRows(session.id, slot.id, row, [rowDef || { type: 'normal' }]);
      if (loggedSet) actions.logSets(session.id, [loggedSet], { keepTs: true });
    } } });
  };
  const removeSlot = (slot) => {
    const idx = slots.findIndex(s => s.id === slot.id);
    const loggedSets = setsForSlot(session, slot);
    editSlots(cur => cur.filter(s => s.id !== slot.id));
    toast(`Removed ${slot.name}`, 'default', { action: { label: 'Undo', run: () => {
      editSlots(cur => { const c = [...cur]; c.splice(Math.min(idx, c.length), 0, slot); return c; });
      if (loggedSets.length) actions.logSets(session.id, loggedSets, { keepTs: true });
    } } });
  };

  const quickLog = (slot, row) => {
    const k = `${slot.id}:${row}`;
    if (Date.now() - (tapGuard.current[k] || 0) < 450) return; // ignore accidental double taps
    tapGuard.current[k] = Date.now();
    const done = setsForSlot(session, slot).find(s => s.setIdx === row);
    if (done) { actions.unlogSet(session.id, slot.id, row); return; }
    const s = sugg(slot, row);
    if (s.cardio) {
      if (!(s.dist > 0) && !(s.dur > 0)) { openPad(slot, row, 'dist'); return; }
      commitSet(slot, row, { dist: s.dist, dur: s.dur, rpe: null });
      return;
    }
    if (!(s.reps > 0) || s.w == null) { openPad(slot, row, 'w'); return; }
    // Timed holds: ✓ starts the hold countdown; it logs itself at the target
    // (or the time held, on Done). "Log 0:45 instead" covers logging afterwards.
    if (slot.unit === 'sec') { openPad(slot, row, 'reps', null, { timer: 'down' }); return; }
    // AMRAP ("5+"): the rep count IS the data point (it drives TM progression),
    // so ✓ asks for it instead of logging the minimum.
    if (slot.rows[row]?.amrap) { openPad(slot, row, 'reps'); return; }
    commitSet(slot, row, { w: s.w, reps: s.reps, rpe: null });
  };

  // Inline keypad (Hevy/Strong style): docked at the bottom, no backdrop, the
  // row stays visible above it. The full sheet (RPE grid, plates) is "More".
  const KGF = 0.45359237;
  // `values` re-opens the pad with what the user had typed (typo "Fix it").
  const openPad = (slot, row, field, values = null, opts = {}) => {
    const done = setsForSlot(session, slot).find(s => s.setIdx === row);
    const s = values || done || sugg(slot, row);
    const rpe = values ? values.rpe ?? null : done ? done.rpe ?? null : null;
    if (isCardio(slot)) {
      const dField = field === 'rpe' ? 'rpe' : field === 'dur' ? 'dur' : 'dist';
      setPad({
        slotId: slot.id, row, cardio: true, field: dField, fresh: true, err: null,
        dText: s.dist > 0 ? String(+metersTo(s.dist, units).toFixed(2)) : '', tDigits: s.dur > 0 ? durDigits(s.dur) : '',
        rpe, logged: !!done, note: null,
      });
    } else {
      const w = s.w == null ? null : s.w;
      setPad({
        slotId: slot.id, row, field: w == null && field !== 'rpe' ? 'w' : field, fresh: true, err: null,
        w0: w, wText: w == null ? '' : fmtWeight(w, units), rText: String(s.reps || slot.repMin || 8),
        rpe, logged: !!done, note: done ? null : s.note,
        amrap: !!slot.rows[row]?.amrap && !done,
        timer: opts.timer ? { mode: opts.timer, target: Math.max(5, s.reps || slot.repMax || 30), startedAt: Date.now(), running: true } : null,
      });
      if (opts.timer) unlockAudioContext();
    }
    setTimeout(() => {
      const el = document.getElementById(`row-${slot.id}-${row}`);
      const sc = scrollRef.current;
      if (!el || !sc) return;
      const r = el.getBoundingClientRect(), box = sc.getBoundingClientRect();
      const limit = window.innerHeight - 330;            // keypad height + gap
      if (r.bottom > limit) sc.scrollBy({ top: r.bottom - limit, behavior: reduceMotion ? 'auto' : 'smooth' });
      else if (r.top < box.top + 8) sc.scrollBy({ top: r.top - box.top - 8, behavior: reduceMotion ? 'auto' : 'smooth' });
    }, 40);
  };
  const padLog = (over) => {
    const p = pad && over ? { ...pad, ...over } : pad; if (!p) return;
    const slot = slots.find(x => x.id === p.slotId); if (!slot) { setPad(null); return; }
    if (p.cardio) {
      const dv = parseFloat(String(p.dText).replace(',', '.'));
      const dist = dv > 0 ? metersFrom(dv, units) : 0;
      const dur = digitsSecs(p.tDigits);
      if (!(dist > 0) && !(dur > 0)) { setPad({ ...p, field: 'dist', fresh: true, errField: 'dist', err: 'Enter a distance or a time' }); return; }
      if (dist > 500000) { setPad({ ...p, field: 'dist', fresh: true, errField: 'dist', err: 'That distance looks off. Check it' }); return; }
      if (dur > 24 * 3600) { setPad({ ...p, field: 'dur', fresh: true, errField: 'dur', err: 'Over 24 hours. Check the time' }); return; }
      setPad(null);
      commitSet(slot, p.row, { dist, dur, rpe: p.rpe });
      return;
    }
    const wv = parseFloat(String(p.wText).replace(',', '.'));
    const reps = parseInt(p.rText, 10);
    // Problems keep the pad open with the field flagged — never discard the edit.
    if (!(wv >= 0) || p.wText === '') { setPad({ ...p, field: 'w', fresh: true, errField: 'w', err: 'Enter a weight' }); return; }
    if (!(reps > 0)) { setPad({ ...p, field: 'reps', fresh: true, errField: 'reps', err: slot.unit === 'sec' ? 'Enter seconds' : 'Enter reps' }); return; }
    // Unchanged text keeps the exact stored value (no kg rounding drift).
    const w = p.w0 != null && p.wText === fmtWeight(p.w0, units) ? p.w0 : (units === 'kg' ? +(wv / KGF).toFixed(4) : wv);
    if (w > MAX_LB) { setPad({ ...p, field: 'w', fresh: true, errField: 'w', err: `Over ${fmtWeight(MAX_LB, units)} ${unitLabel(units)} — check the weight` }); return; }
    const maxReps = slot.unit === 'sec' ? 3600 : MAX_REPS;
    if (reps > maxReps) { setPad({ ...p, field: 'reps', fresh: true, errField: 'reps', err: `Over ${maxReps} — check the number` }); return; }
    setPad(null);
    commitSet(slot, p.row, { w, reps, rpe: p.rpe });
  };

  const editSlots = (fn) => actions.updateSessionSlots(session.id, fn);

  const addExercises = (exs) => {
    if (!exs || !exs.length) return;
    const newSlots = exs.map(ex => slotFromExercise(ex, { targetWeight: 0, catalog: catalogEntryFor(ex.id, catalog) }));
    editSlots(cur => [...cur, ...newSlots]);
    setTimeout(() => {
      const el = document.getElementById(`slot-${newSlots[0].id}`);
      if (el && scrollRef.current) scrollRef.current.scrollTo({ top: el.offsetTop - 8, behavior: 'smooth' });
    }, 150);
  };

  const swapExercise = (slotIdToSwap, ex) => {
    const slot = slots.find(s => s.id === slotIdToSwap);
    if (!slot || !ex) return;
    const loggedRows = new Set(setsForSlot(session, slot).map(s => s.setIdx));
    let targetId = slot.id;
    actions.ensureExercises([ex]);
    const shape = slotFromExercise(ex, { catalog: catalogEntryFor(ex.id, catalog) });
    const sameTrack = (shape.unit === 'sec') === (slot.unit === 'sec') && (shape.track || 'reps') === (slot.track || 'reps');
    if (loggedRows.size === 0) {
      editSlots(cur => cur.map(s => s.id !== slot.id ? s : {
        ...s, exerciseId: ex.id, name: ex.name, group: ex.group || s.group, unit: shape.unit, track: shape.track,
        yt: ex.yt || '', alternates: [], fromProgram: false, targetWeight: 0, variant: null, jointAck: false,
        ...(sameTrack ? {} : { repMin: shape.repMin, repMax: shape.repMax }),
      }));
    } else {
      // Keep what was already logged under the original exercise; remaining rows move to the new one.
      const remaining = slot.rows.filter((_, i) => !loggedRows.has(i));
      const kept = slot.rows.filter((_, i) => loggedRows.has(i));
      const fresh = { ...shape, rows: remaining.length ? remaining : [{ type: 'normal' }], rest: slot.rest, ...(sameTrack ? { repMin: slot.repMin, repMax: slot.repMax } : {}) };
      editSlots(cur => cur.flatMap(s => s.id !== slot.id ? [s] : [{ ...s, rows: kept }, fresh]));
      targetId = fresh.id;
    }
    toast(`Swapped to ${ex.name}`);
    // Bring the swapped card into view (the picker may have left us elsewhere).
    setTimeout(() => {
      const el = document.getElementById(`slot-${targetId}`);
      if (el && scrollRef.current) scrollRef.current.scrollTo({ top: el.offsetTop - 8, behavior: reduceMotion ? 'auto' : 'smooth' });
    }, 150);
  };

  const addWarmups = (slot) => {
    const first = sugg(slot, slot.rows.findIndex(r => r.type !== 'warmup'));
    if (!(first.w > 0)) { toast('Enter a working weight first — warm-ups are computed from it'); return; }
    const ramp = warmupRamp(first.w, { equipment: exById[slot.exerciseId]?.equipment, inc: stepFor(slot, units), bar: units === 'kg' ? 20 / KG : 45 });
    const existing = slot.rows.filter(r => r.type === 'warmup').length;
    const count = Math.max(1, (ramp.length || 2) - existing);
    actions.insertSessionRows(session.id, slot.id, 0, Array.from({ length: count }, () => ({ type: 'warmup' })));
    toast(`${count} warm-up set${count > 1 ? 's' : ''} added`);
  };

  const onFinish = (saveStructure, retroMins) => {
    if (session.retro) {
      // A past workout ends after the duration given; its sets spread over it.
      const end = session.startedAt + Math.max(1, retroMins || 60) * 60000;
      const n = session.sets.length;
      const sets = [...session.sets].sort((a, b) => (a.ts || 0) - (b.ts || 0)).map((st, i) => ({ ...st, ts: session.startedAt + Math.round(((i + 1) / (n + 1)) * (end - session.startedAt)) }));
      actions.updateSession(session.id, { sets, rest: null });
      actions.completeSession(session.id, { saveStructure, completedAt: end });
      replaceRoute('workout-summary', { sessionId: session.id });
      return;
    }
    if (session.rest) actions.updateSession(session.id, { rest: null });
    actions.completeSession(session.id, { saveStructure });
    replaceRoute('workout-summary', { sessionId: session.id });
  };
  watchLink.current = { session, slots, units, prefs: state.prefs, focus, title: session.name, sugg, perfFor, kindFor, commit: commitSet, finish: onFinish };

  // ——— Set types: myo-reps / rest-pause / cluster add linked mini-set rows ———
  const setRowType = (slotIdArg, row, type) => {
    const slot = slots.find(s => s.id === slotIdArg); if (!slot) return;
    const before = slot.rows[row]?.type;
    const minis = minisAfter(slot, row);
    const logged = new Set(setsForSlot(session, slot).map(s => s.setIdx));
    if (isTechnique(type)) {
      editSlots(cur => cur.map(s => s.id !== slot.id ? s : { ...s, rows: s.rows.map((r, i) => i === row ? { ...r, type } : (minis.includes(i) ? { ...r, tech: type } : r)) }));
      if (!minis.length) actions.insertSessionRows(session.id, slot.id, row + 1, miniRows(type));
      // Keep logged mini-sets' technique in step.
      setsForSlot(session, slot).filter(st => minis.includes(st.setIdx)).forEach(st => actions.logSet(session.id, { ...st, tech: type }));
      if (!minis.length) toast(`${TECHNIQUES[type].label}: ${TECHNIQUES[type].minis} mini-sets added`);
    } else {
      editSlots(cur => cur.map(s => s.id !== slot.id ? s : { ...s, rows: s.rows.map((r, i) => { if (i !== row) return r; const { tech: _t, ...o } = r; return { ...o, type }; }) }));
      // Leaving a technique drops its unlogged mini-sets (last first so indices hold).
      if (isTechnique(before)) [...minis].reverse().filter(i => !logged.has(i)).forEach(i => actions.deleteSessionRow(session.id, slot.id, i));
    }
    const done = setsForSlot(session, slot).find(s => s.setIdx === row);
    if (done) { const { tech: _t, ...restOf } = done; actions.logSet(session.id, { ...restOf, type, ...(type === 'mini' && slot.rows[row]?.tech ? { tech: slot.rows[row].tech } : {}) }); }
  };
  const addSetTo = (slot, mini) => {
    const last = slot.rows[slot.rows.length - 1];
    const tech = last?.type === 'mini' ? last.tech : isTechnique(last?.type) ? last.type : null;
    editSlots(cur => cur.map(s => s.id !== slot.id ? s : { ...s, rows: [...s.rows, mini && tech ? { type: 'mini', tech } : { type: 'normal' }] }));
  };

  // ——— Per-muscle check-in (pump · soreness · joints) ———
  const answerFeedback = (muscle, patch) => {
    const cur = (session.feedback || {})[muscle] || { pump: null, soreness: null, joint: null };
    const next = { ...cur, ...patch };
    if (next.joint !== 'bad' && next.joint !== 'some') delete next.jointEx;
    actions.updateSession(session.id, { feedback: { ...(session.feedback || {}), [muscle]: next } });
  };
  const skipFeedback = (muscle) => actions.updateSession(session.id, { feedbackSkipped: [...new Set([...(session.feedbackSkipped || []), muscle])] });
  const feedbackOff = () => {
    actions.setPref('muscleFeedback', false);
    toast('Muscle check-ins turned off', 'default', { action: { label: 'Undo', run: () => actions.setPref('muscleFeedback', true) } });
  };
  const muscles = sessionMuscles(session, slots, exIndex);
  const fbDone = (m) => { const f = (session.feedback || {})[m]; return !!f && f.pump != null && f.joint != null && (f.soreness != null || !soreAsk.has(m)); };
  const skipped = new Set(session.feedbackSkipped || []);
  // Inline: muscles whose work is finished, not skipped (complete ones stay as a one-line receipt).
  const fbBySlot = {};
  if (askFeedback) for (const m of muscles) if (m.done && m.logged > 0 && !skipped.has(m.muscle)) (fbBySlot[m.lastSlotId] = fbBySlot[m.lastSlotId] || []).push(m);
  const fbExercises = (m) => m.slotIds.map(id => slots.find(s => s.id === id)).filter(Boolean).map(s => ({ id: s.exerciseId, name: s.variant?.name || s.name }));
  // Finish sheet: everything trained today that still has an open question.
  const fbAtFinish = askFeedback ? muscles.filter(m => m.logged > 0 && !skipped.has(m.muscle) && !fbDone(m.muscle)) : [];

  // ——— Body weight: asked once, only when a bodyweight lift is in the workout ———
  const firstBwSlot = !(bwNow > 0) && !state.prefs.bwAsked ? slots.find(s => isBwLoad(kindFor(s).load) && kindFor(s).track !== 'distance-time') : null;
  const saveBodyweight = (v) => {
    const lb = units === 'kg' ? v / KG : v;
    actions.addBodyEntry({ date: localDateStr(Date.now()), weight: +lb.toFixed(2), bf: null });
    actions.setPref('bwAsked', true);
  };

  // ——— Quick log targets ———
  // Into a slot of this workout: fill its open rows first, then add rows.
  const quickToSlot = (slot, sets) => {
    const logged = new Set(setsForSlot(session, slot).map(s => s.setIdx));
    const free = slot.rows.map((_, i) => i).filter(i => !logged.has(i) && slot.rows[i].type !== 'warmup' && slot.rows[i].type !== 'mini');
    const need = Math.max(0, sets.length - free.length);
    const rows = [...free, ...Array.from({ length: need }, (_, k) => slot.rows.length + k)];
    const items = sets.map((st, k) => {
      const row = rows[k];
      if (kindFor(slot).track === 'distance-time') return { row, payload: { w: 0, reps: 0, rpe: st.rpe, dist: st.dist, dur: st.dur } };
      const w = st.w == null ? (sugg(slot, Math.min(row, slot.rows.length - 1)).w ?? 0) : st.w;
      return { row, payload: { w, reps: st.reps || st.dur || 0, rpe: st.rpe } };
    });
    commitBatch(slot, items);
  };
  // An exercise named but not in the workout: add it, then log into it.
  const addAndLog = (ex, sets) => {
    if (!ex || !sets.length) return;
    actions.ensureExercises([ex]);
    const cat = catalogEntryFor(ex.id, catalog);
    const base = slotFromExercise(ex, { targetWeight: 0, catalog: cat });
    const slot = { ...base, rows: Array.from({ length: Math.max(1, sets.length) }, () => ({ type: 'normal' })) };
    const kind = slotKind(slot, exById[ex.id] || ex, cat);
    const payloads = sets.map((st, i) => {
      const pl = kind.track === 'distance-time'
        ? { w: 0, reps: 0, rpe: st.rpe, dist: st.dist, dur: st.dur }
        : { w: st.w == null ? 0 : st.w, reps: st.reps || st.dur || 0, rpe: st.rpe };
      const p = setPayload(slot, i, pl);
      return { ...p, ...(isBwLoad(kind.load) && bwNow > 0 ? { bw: bwNow } : {}) };
    }).filter(p => p.reps > 0 || p.dist > 0 || p.dur > 0);
    if (!payloads.length) { toast('Nothing to log there'); return; }
    editSlots(cur => [...cur, slot]);
    actions.logSets(session.id, payloads);
    if (state.prefs.haptics) tryVibrate(12);
    toast(`Added ${ex.name} · logged ${payloads.length} ${kind.track === 'distance-time' ? 'interval' : 'set'}${payloads.length > 1 ? 's' : ''}`);
    setTimeout(() => {
      const el = document.getElementById(`slot-${slot.id}`);
      if (el && scrollRef.current) scrollRef.current.scrollTo({ top: el.offsetTop - 8, behavior: reduceMotion ? 'auto' : 'smooth' });
    }, 160);
  };

  const rest = session.rest && session.rest.endsAt > now ? session.rest : null;
  const restLeft = rest ? Math.ceil((rest.endsAt - now) / 1000) : 0;
  const changed = session.programId ? structureChanged(state.programs.find(p => p.id === session.programId), session, slots) : false;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: LL.bg, position: 'relative' }}>
      {/* ——— Header ——— */}
      <div style={{ padding: 'calc(env(safe-area-inset-top, 0px) + 10px) 14px 0', background: LL.bg, position: 'relative', zIndex: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button aria-label="Minimize workout" onClick={() => setRoute('home')} style={iconBtn()}>
            <LLIcon.chevDown style={{ width: 22, height: 22 }} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {session.name || 'Workout'}
            </div>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, fontVariantNumeric: 'tabular-nums', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {session.retro
                ? <span style={{ color: LL.fg, fontWeight: 600 }}>Past · {new Date(session.startedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                : <span style={{ color: LL.fg, fontWeight: 600 }}>{fmtClock(elapsed)}</span>}
              {' · '}{fmtShortK(volume, units)} {unitLabel(units)}
            </div>
          </div>
          <button aria-label="Quick log by voice or text" onClick={() => setQuickOpen(true)} style={iconBtn()}>
            <MicIcon />
          </button>
          <button aria-label="Workout options" onClick={() => setDiscardOpen(true)} style={iconBtn()}>
            <LLIcon.more />
          </button>
          <button onClick={() => setFinishOpen(true)} style={{
            height: 40, padding: '0 16px', borderRadius: LL.r.md, border: 'none',
            background: LL.accent, color: LL.accentInk, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, cursor: 'pointer',
          }}>Finish</button>
        </div>
        {/* progress */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
          <div style={{ flex: 1, height: 3, borderRadius: LL.r.xs, background: LL.surface3, overflow: 'hidden' }}>
            <div className="ll-anim" style={{ height: '100%', width: `${totalRows ? (doneRows / totalRows) * 100 : 0}%`, background: LL.accent, transition: 'width .35s ' + LL.ease }} />
          </div>
          <span aria-label={`${doneRows} of ${totalRows} sets done`} style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 600, color: LL.fgMute, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{doneRows}/{totalRows} sets</span>
        </div>
      </div>

      {/* ——— Exercises ——— */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: `12px 12px ${pad ? 360 : 160}px`, position: 'relative' }}>
        {slots.length === 0 && (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: LL.r.pill, background: LL.surface2, color: LL.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
              <LLIcon.dumbbell style={{ width: 30, height: 30 }} />
            </div>
            <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg }}>Empty workout</div>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 6 }}>Add exercises as you go — your history and suggestions come along.</div>
          </div>
        )}
        {slots.length > 0 && !session.deload && !session.retro && (
          <CheckIn
            session={session}
            canLighten={session.sets.length === 0 && !session.lighter}
            onPick={(feel) => {
              actions.updateSession(session.id, { checkin: feel });
            }}
            onLighter={() => {
              editSlots(cur => cur.map(s => {
                const work = s.rows.filter(r => r.type !== 'warmup').length;
                if (work <= 2) return s;
                const lastIdx = s.rows.map(r => r.type).lastIndexOf('normal');
                return lastIdx < 0 ? s : { ...s, rows: s.rows.filter((_, i) => i !== lastIdx) };
              }));
              actions.updateSession(session.id, { checkin: 'rough', lighter: true });
              toast('Lighter session: one less set per lift, 5% lighter');
            }}
          />
        )}
        {session.deload && (
          <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: LL.r.md, background: `color-mix(in oklab, ${LL.info} 14%, ${LL.surface})`, border: `1px solid color-mix(in oklab, ${LL.info} 35%, ${LL.line})`, fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fg }}>
            <b>Deload week</b> — fewer sets and ~10% lighter loads. Stop every set 3+ reps short of failure.
          </div>
        )}
        {slots.map((slot, i) => (
          <SlotCard
            key={slot.id}
            slot={slot}
            prevSlot={slots[i - 1]}
            nextSlot={slots[i + 1]}
            session={session}
            units={units}
            exMeta={exById[slot.exerciseId]}
            focus={focus}
            flash={flash}
            prRows={prRows}
            lastPerf={perfFor(slot)}
            ratesEffort={ratesEffort}
            dupTag={dupTag(slot)}
            bwDisplay={isBodyweight(slot, exById[slot.exerciseId]?.equipment, kindFor(slot))}
            kind={kindFor(slot)}
            effort={effort}
            bwNow={bwNow}
            sugg={(row) => sugg(slot, row)}
            onCheck={(row) => quickLog(slot, row)}
            onNum={(row, field) => openPad(slot, row, field)}
            editing={pad && pad.slotId === slot.id ? pad : null}
            padOpen={!!pad}
            onRpe={(row) => openPad(slot, row, 'rpe')}
            onType={(row) => setTypeSheet({ slotId: slot.id, row })}
            onDeleteRow={(row) => deleteRow(slot.id, row)}
            onAddSet={(mini) => addSetTo(slot, mini)}
            onMenu={() => setMenuSlot(slot.id)}
            onOpenDetail={() => setRoute('exercise', { exerciseId: slot.exerciseId })}
            onVideo={() => setVideo(formFor(slot, exById))}
            top={<>
              {firstBwSlot && firstBwSlot.id === slot.id && (
                <BodyweightAsk units={units} name={slot.variant?.name || slot.name} onSave={saveBodyweight} onDismiss={() => actions.setPref('bwAsked', true)} />
              )}
              {!slot.jointAck && !setsForSlot(session, slot).length && (
                <JointSwap state={state} slot={slot} onSwap={(ex) => swapExercise(slot.id, ex)} onKeep={() => editSlots(cur => cur.map(s => s.id !== slot.id ? s : { ...s, jointAck: true }))} />
              )}
            </>}
            bottom={(fbBySlot[slot.id] || []).length ? (
              <div id={`fb-${slot.id}`} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 10px 10px' }}>
                {fbBySlot[slot.id].map(m => (
                  <MuscleCheck key={m.muscle} muscle={m.muscle} value={(session.feedback || {})[m.muscle]} askSoreness={soreAsk.has(m.muscle)}
                    exercises={fbExercises(m)} done={fbDone(m.muscle)}
                    onAnswer={(patch) => answerFeedback(m.muscle, patch)} onSkip={() => skipFeedback(m.muscle)} onOff={feedbackOff} />
                ))}
              </div>
            ) : null}
          />
        ))}

        <button data-ll-primary onClick={() => setPicker({ mode: 'add' })} style={{
          width: '100%', height: 52, marginTop: 6, borderRadius: LL.r.md,
          background: LL.accentSoft, color: LL.accentText, border: `1px solid color-mix(in oklab, ${LL.accent} 35%, transparent)`,
          fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <LLIcon.plus style={{ width: 18, height: 18 }} /> Add exercise
        </button>
      </div>

      {/* ——— Rest bar ——— */}
      <RestBar
        rest={rest}
        left={restLeft}
        onAdjust={(d) => {
          if (!rest) return;
          const endsAt = Math.max(Date.now() + 1000, rest.endsAt + d * 1000);
          actions.updateSession(session.id, { rest: { ...rest, endsAt, total: Math.max(1, rest.total + d), adapted: false } });
        }}
        onSkip={() => actions.updateSession(session.id, { rest: null })}
        onReset={() => {
          if (!rest || !rest.prescribed) return;
          const started = rest.endsAt - rest.total * 1000;
          actions.updateSession(session.id, { rest: { ...rest, endsAt: Math.max(Date.now() + 1000, started + rest.prescribed * 1000), total: rest.prescribed, adapted: false } });
        }}
      />

      {pad && (
        <KeypadDock
          pad={pad}
          slot={slots.find(x => x.id === pad.slotId)}
          units={units}
          kind={kindFor(slots.find(x => x.id === pad.slotId) || {})}
          effort={effort}
          bwNow={bwNow}
          sound={state.prefs.sound}
          haptics={state.prefs.haptics}
          rest={rest}
          restLeft={restLeft}
          onRest={(d) => {
            if (!rest) return;
            if (d === 'skip') { actions.updateSession(session.id, { rest: null }); return; }
            const endsAt = Math.max(Date.now() + 1000, rest.endsAt + d * 1000);
            actions.updateSession(session.id, { rest: { ...rest, endsAt, total: Math.max(1, rest.total + d), adapted: false } });
          }}
          onChange={(patch) => setPad(p => p && ({ ...p, ...patch }))}
          onClose={() => setPad(null)}
          onLog={padLog}
          onPlates={usesPlates(exById[slots.find(x => x.id === pad.slotId)?.exerciseId]?.equipment) ? (lb) => setPlateFor(lb) : null}
        />
      )}

      {/* ——— Sheets ——— */}
      <SetTypeSheet
        open={!!typeSheet}
        current={typeSheet ? slots.find(s => s.id === typeSheet.slotId)?.rows[typeSheet.row]?.type : null}
        tech={typeSheet ? slots.find(s => s.id === typeSheet.slotId)?.rows[typeSheet.row]?.tech : null}
        cardio={typeSheet ? kindFor(slots.find(s => s.id === typeSheet.slotId) || {}).track !== 'reps' : false}
        onClose={() => setTypeSheet(null)}
        onPick={(type) => {
          const t = typeSheet; setTypeSheet(null);
          setRowType(t.slotId, t.row, type);
        }}
        onDelete={() => {
          const t = typeSheet; setTypeSheet(null);
          deleteRow(t.slotId, t.row);
        }}
      />

      <SlotMenu
        open={!!menuSlot}
        slot={slots.find(s => s.id === menuSlot)}
        index={slots.findIndex(s => s.id === menuSlot)}
        count={slots.length}
        hasNext={slots.findIndex(s => s.id === menuSlot) < slots.length - 1}
        equipment={exById[slots.find(s => s.id === menuSlot)?.exerciseId]?.equipment}
        onClose={() => setMenuSlot(null)}
        onAction={(a, arg) => {
          const slot = slots.find(s => s.id === menuSlot);
          setMenuSlot(null);
          if (!slot) return;
          const idx = slots.findIndex(s => s.id === slot.id);
          if (a === 'swap') setPicker({ mode: 'swap', slotId: slot.id });
          else if (a === 'alt') editSlots(cur => cur.map(s => s.id !== slot.id ? s : { ...s, variant: arg }));
          else if (a === 'warmup') addWarmups(slot);
          else if (a === 'plates') setPlateFor(sugg(slot, Math.max(0, nextRow(session, slot))).w || 0);
          else if (a === 'reorder') setReorderOpen(true);
          else if (a === 'rest') setRestPick(slot.id);
          else if (a === 'note') setNoteSlot(slot.id);
          else if (a === 'detail') setRoute('exercise', { exerciseId: slot.exerciseId });
          else if (a === 'yt') setVideo(formFor(slot, exById));
          else if (a === 'up' && idx > 0) editSlots(cur => { const c = [...cur]; [c[idx - 1], c[idx]] = [c[idx], c[idx - 1]]; return c; });
          else if (a === 'down' && idx < slots.length - 1) editSlots(cur => { const c = [...cur]; [c[idx + 1], c[idx]] = [c[idx], c[idx + 1]]; return c; });
          else if (a === 'superset') {
            const next = slots[idx + 1]; if (!next) return;
            const gid = slot.supersetId || next.supersetId || ('ss_' + slotId());
            editSlots(cur => cur.map(s => (s.id === slot.id || s.id === next.id) ? { ...s, supersetId: gid } : s));
            toast(`Superset: ${slot.name} + ${next.name}`);
          } else if (a === 'unsuperset') editSlots(cur => cur.map(s => s.id === slot.id ? { ...s, supersetId: null } : s));
          else if (a === 'remove') removeSlot(slot);
        }}
      />

      <LLSheet open={!!typo} onClose={() => setTypo(null)} title="Is that right?" subtitle={typo?.slot?.name || ''}>
        {typo && (() => {
          const ws = [...new Set(typo.items.map(it => it.payload.w))];
          const many = typo.items.length > 1;
          const wLabel = ws.map(w => `${fmtWeight(w, units)} ${unitLabel(units)}`).join(', ');
          return (
            <div>
              <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fg, lineHeight: 1.45 }}>
                {many ? <>These {typo.items.length} sets at <b>{wLabel}</b></> : <b>{wLabel}</b>}{' '}
                {typo.usual > 0
                  ? <>{many ? 'are' : 'is'} very different from your usual {fmtWeight(typo.usual, units)} {unitLabel(units)} on this lift.</>
                  : <>{many ? 'are' : 'is'} unusually heavy for this exercise.</>}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button onClick={() => {
                  const t = typo; setTypo(null);
                  if (t.items.length === 1) openPad(t.slot, t.items[0].row, 'w', t.items[0].payload); else setQuickOpen(true);
                }} style={{ flex: 1, height: 50, borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: LL.surface2, color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer' }}>Fix it</button>
                <button onClick={() => {
                  const t = typo; setTypo(null);
                  if (t.items.length === 1) commitSet(t.slot, t.items[0].row, t.items[0].payload, { confirmed: true });
                  else commitBatch(t.slot, t.items, { confirmed: true });
                }} style={{ flex: 1, height: 50, borderRadius: LL.r.md, border: 'none', background: LL.accent, color: LL.accentInk, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, cursor: 'pointer' }}>{many ? `Log all ${typo.items.length}` : 'Log it anyway'}</button>
              </div>
            </div>
          );
        })()}
      </LLSheet>

      <ExercisePicker
        similarTo={picker?.mode === 'swap' ? slots.find(s => s.id === picker.slotId)?.exerciseId : undefined}
        open={!!picker}
        multi={picker?.mode === 'add'}
        title={picker?.mode === 'swap' ? 'Swap exercise' : picker?.mode === 'quick' ? `Log ${picker.sets.length} set${picker.sets.length === 1 ? '' : 's'} to…` : 'Add exercises'}
        onClose={() => setPicker(null)}
        onPick={(exs) => {
          const p = picker; setPicker(null);
          if (!exs || !exs.length) return;
          if (p.mode === 'swap') swapExercise(p.slotId, exs[0]);
          else if (p.mode === 'quick') {
            const inWorkout = slots.find(s => s.exerciseId === exs[0].id);
            if (inWorkout) quickToSlot(inWorkout, p.sets); else addAndLog(exs[0], p.sets);
          } else addExercises(exs);
        }}
      />

      <PlateSheet open={plateFor != null} weightLb={plateFor || 0} units={units} barLb={state.prefs.barWeight} platesAvail={state.prefs.plates?.[units]} onClose={() => setPlateFor(null)} />

      <RestPickSheet
        open={!!restPick}
        value={slots.find(s => s.id === restPick)?.rest}
        onClose={() => setRestPick(null)}
        onPick={(v) => { const id = restPick; setRestPick(null); editSlots(cur => cur.map(s => s.id !== id ? s : { ...s, rest: v })); }}
      />

      <NoteSheet
        open={!!noteSlot}
        slot={slots.find(s => s.id === noteSlot)}
        exercise={exById[slots.find(s => s.id === noteSlot)?.exerciseId]}
        onClose={() => setNoteSlot(null)}
        onSave={(text) => {
          const slot = slots.find(s => s.id === noteSlot); setNoteSlot(null);
          if (slot && exById[slot.exerciseId]) actions.updateExercise(slot.exerciseId, { note: text });
        }}
      />

      <FinishSheet
        open={finishOpen}
        session={session}
        slots={slots}
        elapsed={elapsed}
        volume={volume}
        units={units}
        changed={changed}
        dayName={session.name}
        onClose={() => setFinishOpen(false)}
        onFinish={onFinish}
        checkin={simple ? (
          <HowDidItFeel value={session.feel} onPick={(v) => actions.updateSession(session.id, { feel: v })} />
        ) : fbAtFinish.length ? (
          <FinishCheckIn muscles={fbAtFinish} feedback={session.feedback || {}} soreAsk={soreAsk} exercisesOf={fbExercises}
            onAnswer={answerFeedback} onSkip={skipFeedback} onOff={feedbackOff} />
        ) : null}
      />

      <LLSheet open={discardOpen} onClose={() => setDiscardOpen(false)} title="Workout options">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <MenuGroup>
            <MenuItem icon={LLIcon.chevDown} label="Minimize" sub="Keep it running in the background" onClick={() => { setDiscardOpen(false); setRoute('home'); }} />
            {slots.length > 1 && <MenuItem icon={LLIcon.grip} label="Reorder exercises" sub="Drag to change the order" onClick={() => { setDiscardOpen(false); setReorderOpen(true); }} />}
            {typeof Notification !== 'undefined' && Notification.permission === 'default' && (
              <MenuItem icon={BellIcon} label="Notify me when rest ends" sub="Even with the screen off" onClick={async () => { await requestNotificationPermission(); setDiscardOpen(false); }} />
            )}
          </MenuGroup>
          <MenuGroup>
            {!simple && (
              <SettingRow icon={GaugeIcon} label="Effort scale" sub={effort === 'rir' ? 'Reps in reserve (RIR = 10 − RPE)' : 'Rate of perceived exertion'}>
                <Segmented value={effort} options={[['rpe', 'RPE'], ['rir', 'RIR']]} onChange={(v) => actions.setPref('effortScale', v)} label="Effort scale" />
              </SettingRow>
            )}
            {!simple && (
              <SettingRow icon={LLIcon.body} label="Muscle check-ins" sub="Pump, soreness and joints after each muscle" onClick={() => actions.setPref('muscleFeedback', !askFeedback)}>
                <LLSwitchTrack on={askFeedback} />
              </SettingRow>
            )}
            <SettingRow icon={LLIcon.note} label="Simple mode" sub={simple ? 'Plain words, fewer numbers' : 'Off — every number and term on show'} onClick={() => setSimple(!simple)}>
              <LLSwitchTrack on={simple} />
            </SettingRow>
          </MenuGroup>
          <MenuGroup>
            <MenuItem icon={LLIcon.trash} tone="danger" label="Discard workout" sub={`${session.sets.length} set${session.sets.length === 1 ? '' : 's'} logged · tap twice to confirm`} onClick={() => {
              if (!window.__llConfirmDiscard) { window.__llConfirmDiscard = true; toast('Tap again to discard this workout'); setTimeout(() => { window.__llConfirmDiscard = false; }, 3000); return; }
              window.__llConfirmDiscard = false;
              setDiscardOpen(false);
              actions.cancelSession(session.id);
              replaceRoute('home');
            }} />
          </MenuGroup>
        </div>
      </LLSheet>

      <QuickLogSheet
        open={quickOpen}
        slots={slots}
        focusSlotId={focus?.slotId || slots[0]?.id}
        units={units}
        kindFor={kindFor}
        effort={effort}
        onAddAndLog={(ex, sets) => { setQuickOpen(false); addAndLog(ex, sets); }}
        onPickOther={(sets) => { setQuickOpen(false); setPicker({ mode: 'quick', sets }); }}
        onClose={() => setQuickOpen(false)}
        onLog={(slot, sets) => { setQuickOpen(false); quickToSlot(slot, sets); }}
      />

      <ReorderSheet
        open={reorderOpen}
        slots={slots}
        onClose={() => setReorderOpen(false)}
        onApply={(order) => { setReorderOpen(false); editSlots(cur => order.map(id => cur.find(s => s.id === id)).filter(Boolean).concat(cur.filter(s => !order.includes(s.id)))); }}
      />

      <FormSheet open={!!video} onClose={() => setVideo(null)} exerciseId={video?.exerciseId} name={video?.name || ''} link={video?.link || ''} />
    </div>
  );
}

// Pre-workout check-in: one fixed-height row for the whole session, so
// answering it (or logging the first set) never moves the set rows under the
// thumb. "Run down" / low readiness offers a lighter session in a sheet.
const FEEL = { great: 'Great', normal: 'Normal', rough: 'Run down' };
function CheckIn({ session, canLighten, onPick, onLighter }) {
  const { state } = useApp();
  const hist = historyOf(state.sessions);
  const r = React.useMemo(() => readiness(state), [hist]); // eslint-disable-line react-hooks/exhaustive-deps
  const [ask, setAsk] = React.useState(false);
  const answered = session.checkin || null;
  const lowScore = r.score != null && r.score < 50;
  const pick = (id) => {
    if (id === 'rough' && canLighten) { setAsk(true); return; }
    onPick(id);
  };
  const box = {
    height: 56, marginBottom: 12, padding: '0 8px 0 14px', borderRadius: LL.r.md, background: LL.surface, border: `1px solid ${LL.line}`,
    display: 'flex', alignItems: 'center', gap: 10, boxSizing: 'border-box',
  };
  return (
    <>
      {answered ? (
        <div style={box}>
          <span style={{ width: 28, height: 28, borderRadius: LL.r.md, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: LL.accentSoft, color: LL.accentText }}>
            <LLIcon.check style={{ width: 16, height: 16 }} />
          </span>
          <div style={{ flex: 1, minWidth: 0, fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <span style={{ color: LL.fg, fontWeight: 600 }}>Feeling {FEEL[answered]?.toLowerCase() || answered}</span>
            {session.lighter ? ' · lighter session' : r.score != null ? ` · readiness ${r.score}` : ''}
          </div>
        </div>
      ) : (
        <div style={box} role="group" aria-label="How do you feel today?">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, color: LL.fg, whiteSpace: 'nowrap' }}>How do you feel?</div>
            {r.score != null && (
              <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, color: lowScore ? LL.warn : LL.fgDim, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Readiness {r.score} · {r.label}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', padding: 3, gap: 2, borderRadius: LL.r.sm, background: LL.surface2, flexShrink: 0 }}>
            {Object.entries(FEEL).map(([id, label]) => (
              <button key={id} onClick={() => pick(id)} style={{
                height: 34, padding: '0 clamp(6px, 2.2vw, 10px)', borderRadius: LL.r.sm, border: 'none', cursor: 'pointer', background: 'transparent',
                color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, whiteSpace: 'nowrap',
              }}>{label}</button>
            ))}
          </div>
        </div>
      )}
      <LLSheet open={ask} onClose={() => setAsk(false)} title="Make today lighter?" subtitle={r.score != null ? `Readiness ${r.score} · ${r.label}` : undefined}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fgMute, lineHeight: 1.5 }}>
          {lowScore && r.reasons?.[0] ? `${r.reasons[0]} ` : 'Training through fatigue at full volume rarely pays off. '}
          A lighter session drops one set per lift and takes 5% off the loads. It keeps the habit without digging a deeper hole.
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={() => { setAsk(false); onPick('rough'); }} style={{ flex: 1, height: 50, borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: 'transparent', color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer' }}>Train as planned</button>
          <button onClick={() => { setAsk(false); onLighter(); }} style={{ flex: 1, height: 50, borderRadius: LL.r.md, border: 'none', background: LL.accent, color: LL.accentInk, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, cursor: 'pointer' }}>Make it lighter</button>
        </div>
      </LLSheet>
    </>
  );
}

function iconBtn(extra = {}) {
  return {
    width: 40, height: 40, borderRadius: LL.r.md, flexShrink: 0,
    background: LL.surface, border: `1px solid ${LL.line}`, color: LL.fg,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer', ...extra,
  };
}

// Inset-group action rows (iOS style): 28px tinted icon tile, 16/600 title.
function MenuGroup({ children }) {
  return (
    <div style={{ background: LL.surface2, border: `1px solid ${LL.line}`, borderRadius: LL.r.md, overflow: 'hidden' }}>
      {/* first row has no top hairline */}
      <div style={{ marginTop: -1 }}>{children}</div>
    </div>
  );
}
function MenuItem({ icon, label, sub, tone, onClick }) {
  const danger = tone === 'danger';
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 12, minHeight: 52, padding: '8px 14px', cursor: 'pointer', textAlign: 'left',
      background: 'transparent', border: 'none', borderTop: `1px solid ${LL.line}`, width: '100%',
    }}>
      <span style={{
        width: 32, height: 32, borderRadius: LL.r.sm, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `color-mix(in oklab, ${danger ? LL.danger : LL.accent} 14%, ${LL.surface2})`, color: danger ? LL.danger : LL.accentText,
      }}>{React.createElement(icon, { style: { width: 18, height: 18 } })}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 600, color: danger ? LL.danger : LL.fg }}>{label}</div>
        {sub && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 1 }}>{sub}</div>}
      </span>
    </button>
  );
}
function BellIcon(p) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}>
      <path d="M5 13.5V9a5 5 0 0110 0v4.5l1.5 1.5h-13L5 13.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M8.3 17a1.8 1.8 0 003.4 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// ——— Slot card ——————————————————————————————————————————————————————

// The RPE column only appears once effort is part of this lift (logged, or
// prescribed by the program); until then RPE lives in the keypad's third tab.
const GRID_RPE = '34px minmax(0,1fr) 74px 56px 44px 46px';
const GRID = '34px minmax(0,1fr) 80px 62px 48px';

// Compact "Previous" text: "185 × 8", "BW+25 × 8", "BW × 12", "BW+25 · 0:45", "5.00 · 25:30".
function prevText(prev, kind, units, bwDisplay) {
  if (!prev) return '—';
  if (bwDisplay && !isBwLoad(kind.load) && kind.track === 'reps' && !(prev.w > 0)) return `BW × ${prev.reps}`;
  if (kind.track === 'distance-time') {
    const parts = [];
    if (prev.dist > 0) parts.push(fmtDistance(prev.dist, units));
    if (prev.dur > 0) parts.push(fmtDuration(prev.dur));
    return parts.join(' · ') || '—';
  }
  const bw = isBwLoad(kind.load);
  const load = bw ? loadText(prev.w, kind.load, units).replace(/ /g, '') : fmtWeight(prev.w, units);
  // Holds: the load in short form ("+25 · 0:45") so it fits at 360 px.
  if (kind.track === 'time') {
    const short = prev.w > 0 ? `${kind.load === 'assisted' ? '−' : bw ? '+' : ''}${fmtWeight(prev.w, units)}` : (bw ? 'BW' : '');
    return `${short ? short + ' · ' : ''}${fmtDuration(prev.reps || prev.dur || 0)}`;
  }
  return `${load} × ${prev.reps}`;
}

function SlotCard({ slot, dupTag, ratesEffort, bwDisplay, prevSlot, nextSlot, session, units, exMeta, focus, flash, prRows, lastPerf, sugg, onCheck, onNum, onRpe, onType, onDeleteRow, onAddSet, onMenu, onOpenDetail, onVideo, editing, padOpen, kind, effort, bwNow, top, bottom }) {
  const logged = setsForSlot(session, slot);
  const byRow = Object.fromEntries(logged.map(s => [s.setIdx, s]));
  const k = kind || { load: 'weighted', track: slot.unit === 'sec' ? 'time' : 'reps' };
  const isTimed = k.track === 'time';
  const cardio = k.track === 'distance-time';
  const bwLoad = isBwLoad(k.load) && !cardio;
  const inSuperset = !!slot.supersetId;
  const ssStart = inSuperset && prevSlot?.supersetId !== slot.supersetId;
  const ssEnd = inSuperset && nextSlot?.supersetId !== slot.supersetId;
  const hasVideo = !!demoFor(formFor(slot, exMeta ? { [slot.exerciseId]: exMeta } : {}));
  const done = slot.rows.every((_, i) => byRow[i]);
  const primary = exMeta?.primary || [slot.group];
  const secondary = exMeta?.secondary || [];
  let workCounter = 0;
  // Simple mode hides the effort column unless the lifter opted in.
  const { showEffort } = useSimple();
  const showRpe = showEffort && (!!slot.rpeTarget || logged.some(s => s.rpe != null) || slot.rows.some(r => r.rpe != null || r.rir != null)
    || (editing && editing.rpe != null)
    || (!cardio && (ratesEffort || (Array.isArray(lastPerf) && lastPerf.some(p => p && p.rpe != null)))));
  const grid = showRpe ? GRID_RPE : GRID;
  const wHead = cardio ? distUnit(units) : bwLoad ? (k.load === 'assisted' ? `−${unitLabel(units)}` : `+${unitLabel(units)}`) : bwDisplay ? `+${unitLabel(units)}` : unitLabel(units);
  const heads = ['Set', 'Previous', wHead, cardio || isTimed ? 'Time' : 'Reps', ...(showRpe ? [effort === 'plain' ? 'Left' : effort === 'rir' ? 'RIR' : 'RPE'] : []), ''];
  const techs = [...new Set(slot.rows.filter(r => isTechnique(r.type)).map(r => TECHNIQUES[r.type].label.toLowerCase()))];
  const last = slot.rows[slot.rows.length - 1];
  const canMini = last && (last.type === 'mini' || isTechnique(last.type));
  const nMain = slot.rows.filter(isMainRow).length;
  const range = slot.repMin === slot.repMax ? `${slot.repMin}` : `${slot.repMin}–${slot.repMax}`;

  return (
    <div id={`slot-${slot.id}`} style={{
      marginBottom: inSuperset && !ssEnd ? 4 : 12,
      background: LL.surface, border: `1px solid ${LL.line}`,
      borderRadius: inSuperset ? `${ssStart ? LL.r.md : LL.r.xs}px ${ssStart ? LL.r.md : LL.r.xs}px ${ssEnd ? LL.r.md : LL.r.xs}px ${ssEnd ? LL.r.md : LL.r.xs}px` : LL.r.md,
      borderLeft: inSuperset ? `3px solid ${LL.accent}` : `1px solid ${LL.line}`,
      overflow: 'hidden', opacity: done && !bottom ? 0.92 : 1,
    }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px 8px 12px' }}>
        <button onClick={onOpenDetail} aria-label={`${slot.name} details`} style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', flexShrink: 0 }}>
          <div style={{ width: 44, height: 44, borderRadius: LL.r.md, background: LL.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <MuscleThumb primary={primary} secondary={secondary} size={44} />
          </div>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          {ssStart && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, color: LL.accentText, textTransform: 'uppercase', marginBottom: 2 }}>Superset</div>}
          <button onClick={onOpenDetail} style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', maxWidth: '100%' }}>
            <div style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.fg, lineHeight: 1.15, letterSpacing: -0.2 }}>
              {slot.variant?.name || slot.name}
              {dupTag && <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, color: LL.accentText, letterSpacing: 0, marginLeft: 6, whiteSpace: 'nowrap' }}>· {dupTag}</span>}
            </div>
          </button>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap', fontVariantNumeric: 'tabular-nums' }}>
            {cardio
              ? <span>{nMain === 1 ? 'Distance · time' : `${nMain} intervals`}</span>
              : <span>{nMain} × {isTimed ? `${range}s` : range}</span>}
            {bwLoad && <span>· {k.load === 'assisted' ? 'assisted' : bwNow > 0 ? `BW ${fmtWhole(bwNow, units)} ${unitLabel(units)}` : 'bodyweight'}</span>}
            {slot.rpeTarget ? <span>· {effort === 'plain' ? plainEffort(slot.rpeTarget).toLowerCase() : effort === 'rir' ? effortText(slot.rpeTarget, 'rir') : `RPE ${slot.rpeTarget}`}</span> : null}
            {techs.map(t => <span key={t} style={{ color: ink(LL.info) }}>· {t}</span>)}
            {(!cardio || nMain > 1) && <span>· rest {fmtClock(slot.rest)}</span>}
            {slot.variant && <span style={{ color: LL.accentText }}>· alt</span>}
          </div>
        </div>
        {hasVideo && (
          <button aria-label="Watch demo" onClick={onVideo} style={{ ...iconBtn(), width: 36, height: 36, border: 'none', background: 'transparent', color: LL.fgMute }}>
            <LLIcon.video />
          </button>
        )}
        <button aria-label="Exercise options" onClick={onMenu} style={{ ...iconBtn(), width: 36, height: 36, border: 'none', background: 'transparent', color: LL.fgMute }}>
          <LLIcon.more />
        </button>
      </div>

      {top}

      {(exMeta?.note || slot.notes) && (
        <div style={{ margin: '0 12px 8px', padding: '8px 10px', borderRadius: LL.r.sm, background: LL.surface2, fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, lineHeight: 1.4 }}>
          {exMeta?.note || slot.notes}
        </div>
      )}

      {/* column labels */}
      <div style={{ display: 'grid', gridTemplateColumns: grid, alignItems: 'center', padding: '0 8px', height: 22 }}>
        {heads.map((h, i) => (
          <div key={i} style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 600, color: LL.fgDim, textAlign: i === 1 ? 'left' : 'center', paddingLeft: i === 1 ? 6 : 0 }}>{h}</div>
        ))}
      </div>

      {slot.rows.map((r, row) => {
        const st = byRow[row];
        const main = isMainRow(r);
        const wi = main ? workCounter++ : -1;
        const prev = main && lastPerf ? lastPerf[wi] : null;
        const s = st || sugg(row);
        // One focus indicator: while the keypad is open it follows the edited row.
        const isFocus = padOpen ? !!(editing && editing.row === row) : !!(focus && focus.slotId === slot.id && focus.row === row);
        const key = `${slot.id}:${row}`;
        const mini = r.type === 'mini';
        const nextMini = slot.rows[row + 1]?.type === 'mini';
        const label = r.type === 'normal' ? String(wi + 1)
          : mini ? `+${miniNumber(slot, row)}`
          : isTechnique(r.type) ? `${SET_TYPES[r.type].short}${wi + 1}`
          : SET_TYPES[r.type]?.short || '?';
        return (
          <SetRow
            key={row}
            label={label}
            type={r.type}
            rowDef={r}
            rowId={`row-${slot.id}-${row}`}
            editField={editing && editing.row === row ? editing.field : null}
            editPad={editing && editing.row === row ? editing : null}
            grid={grid}
            showRpe={showRpe}
            prev={prev}
            prevLabel={prevText(prev, k, units, bwDisplay)}
            bwDisplay={bwDisplay}
            logged={st}
            w={s.w} reps={s.reps} dist={s.dist} dur={s.dur} rpe={st ? st.rpe : null} auto={!st && s.tier === 'rpe'}
            units={units}
            kind={k}
            effort={effort}
            linkTop={mini}
            linkBottom={nextMini}
            focus={isFocus}
            flashing={flash === key}
            pr={!!prRows[key]}
            onCheck={() => onCheck(row)}
            onW={() => onNum(row, cardio ? 'dist' : 'w')}
            onReps={() => onNum(row, cardio ? 'dur' : 'reps')}
            onRpe={() => onRpe(row)}
            onType={() => onType(row)}
            onDelete={() => onDeleteRow(row)}
          />
        );
      })}

      <div style={{ display: 'flex', gap: 8, padding: '6px 10px 10px' }}>
        {canMini && (
          <button onClick={() => onAddSet(true)} style={{
            flex: 1, height: 38, borderRadius: LL.r.sm, background: `color-mix(in oklab, ${LL.info} 12%, ${LL.surface2})`, border: 'none', color: ink(LL.info),
            fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}><LLIcon.plus style={{ width: 16, height: 16 }} /> Mini-set</button>
        )}
        <button onClick={() => onAddSet(false)} style={{
          flex: 1, height: 38, borderRadius: LL.r.sm, background: LL.surface2, border: 'none', color: LL.fg,
          fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}><LLIcon.plus style={{ width: 16, height: 16 }} /> {cardio ? 'Add interval' : 'Add set'}</button>
      </div>
      {bottom}
    </div>
  );
}

function SetRow({ label, bwDisplay, type, rowDef, rowId, editField, editPad, grid, showRpe, prevLabel, logged, w, reps, dist, dur, rpe, auto, units, kind, effort, linkTop, linkBottom, focus, flashing, pr, onCheck, onW, onReps, onRpe, onType, onDelete }) {
  const k = kind || { load: 'weighted', track: 'reps' };
  const cardio = k.track === 'distance-time';
  const timed = k.track === 'time';
  const bwLoad = isBwLoad(k.load) && !cardio;
  // Engine prescriptions: % of training max, target RPE / RIR, AMRAP ("5+").
  const rx = rowDef || {};
  const rxTag = rx.pct != null && !rx.backoff ? `${Math.round(rx.pct)}%`
    : rx.rpe != null ? (effort === 'plain' ? plainEffortShort(rx.rpe) : effortText(rx.rpe, effort))
    : rx.rir != null ? (effort === 'plain' ? plainEffortShort(10 - rx.rir) : `${rx.rir} RIR`)
    : rx.backoff ? 'back-off' : null;
  const done = !!logged;
  const ghost = !done; // suggested values shown dimmed until logged
  const mini = type === 'mini';
  const techLike = mini || isTechnique(type);
  const typeColor = type === 'warmup' ? LL.warn : type === 'drop' ? LL.info : type === 'failure' ? LL.danger : techLike ? LL.info : LL.fgMute;
  // Swipe left to delete (with Undo toast). Horizontal drags only; vertical
  // scrolling is untouched (touch-action: pan-y).
  const [dx, setDx] = React.useState(0);
  const drag = React.useRef(null);
  const onTouchStart = (e) => { const t = e.touches[0]; drag.current = { x: t.clientX, y: t.clientY, on: false }; };
  const onTouchMove = (e) => {
    const d = drag.current; if (!d) return;
    const t = e.touches[0]; const mx = t.clientX - d.x; const my = t.clientY - d.y;
    if (!d.on) { if (Math.abs(mx) > 12 && Math.abs(mx) > Math.abs(my) * 1.5 && mx < 0) d.on = true; else if (Math.abs(my) > 12) { drag.current = null; return; } }
    if (d.on) setDx(Math.max(-140, Math.min(0, mx)));
  };
  const onTouchEnd = () => {
    const was = drag.current && drag.current.on; drag.current = null;
    if (was && dx < -84) { setDx(0); onDelete && onDelete(); return; }
    setDx(0);
  };
  const cell = (content, onClick, aria, extra = {}) => (
    <button aria-label={aria} onClick={onClick} style={{
      height: 40, margin: '0 2px', borderRadius: LL.r.sm, border: 'none', cursor: 'pointer',
      background: done ? 'transparent' : LL.surface2,
      fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: ghost ? 600 : 700, fontVariantNumeric: 'tabular-nums',
      color: ghost ? LL.fgMute : LL.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, minWidth: 0, ...extra,
    }}>{content}</button>
  );
  const editRing = { color: LL.fg, boxShadow: `inset 0 0 0 2px ${LL.accent}`, background: LL.surface2 };
  const two = (top, sub, subColor = LL.accentText) => (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.05, minWidth: 0 }}>
      <span>{top}</span>
      {sub && <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 600, color: subColor, marginTop: 1, letterSpacing: 0.2, whiteSpace: 'nowrap' }}>{sub}</span>}
    </span>
  );

  // ——— load / distance cell ———
  let firstCell;
  if (cardio) {
    const shown = editField ? (editPad?.dText || '—') : dist > 0 ? fmtDistance(dist, units) : '—';
    firstCell = cell(shown, onW, `Distance in ${distUnit(units)}`, editField === 'dist' ? editRing : !(dist > 0) && !editField ? { color: LL.fgDim } : {});
  } else {
    const wText = editField ? (editPad?.wText || '') : null;
    let top;
    if (editField) top = bwLoad ? (wText && +wText > 0 ? `${k.load === 'assisted' ? '−' : '+'}${wText}` : 'BW') : (wText || '—');
    else if (w == null) top = '—';
    else if (bwLoad) top = w > 0 ? `${k.load === 'assisted' ? '−' : '+'}${fmtWeight(w, units)}` : 'BW';
    else if (bwDisplay && !(w > 0)) top = 'BW'; // bodyweight movement logged without load: never "0 lb"
    else top = fmtWeight(w, units);
    const bwWord = top === 'BW';
    firstCell = cell(two(<span style={bwWord ? { fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, letterSpacing: 0.3, color: done ? LL.fg : LL.fgMute } : undefined}>{top}</span>, rxTag),
      onW, w == null ? 'Weight (enter)' : bwLoad ? (k.load === 'assisted' ? 'Assistance' : 'Added weight') : 'Weight',
      editField === 'w' ? editRing : auto ? { color: LL.accentText, boxShadow: `inset 0 -2px 0 ${LL.accent}` } : w == null ? { color: LL.fgDim } : {});
  }

  // ——— reps / time cell ———
  let secondCell;
  if (cardio) {
    const t = editField ? (fmtDigits(editPad?.tDigits) || '—') : dur > 0 ? fmtDuration(dur) : '—';
    const pace = !editField && dist > 0 && dur > 0 ? fmtPace(paceSecPerKm(dist, dur), units).replace(' ', '') : null;
    secondCell = cell(two(t, pace, LL.fgMute), onReps, 'Time', editField === 'dur' ? editRing : !(dur > 0) && !editField ? { color: LL.fgDim } : {});
  } else {
    const rv = editField ? (editPad?.rText || '') : reps;
    const shown = rv == null || rv === '' ? '—' : timed ? fmtDuration(+rv) : `${rv}${rx.amrap && !done ? '+' : ''}`;
    secondCell = cell(shown, onReps, timed ? 'Seconds' : rx.amrap && !done ? 'Reps (as many as possible)' : 'Reps',
      editField === 'reps' ? editRing : rx.amrap && !done ? { color: LL.accentText } : {});
  }

  return (
    <div id={rowId} style={{ position: 'relative', margin: '0 4px', borderRadius: LL.r.md, overflow: 'hidden' }}>
      {dx < 0 && (
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: LL.danger, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 18, gap: 6, color: '#fff', fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, opacity: Math.min(1, -dx / 84) }}>
          <LLIcon.trash style={{ width: 18, height: 18 }} /> Delete
        </div>
      )}
    <div className={flashing ? 'll-anim' : undefined}
      onTouchStart={onDelete ? onTouchStart : undefined} onTouchMove={onDelete ? onTouchMove : undefined} onTouchEnd={onDelete ? onTouchEnd : undefined} onTouchCancel={onDelete ? onTouchEnd : undefined}
      style={{
      position: 'relative', touchAction: 'pan-y',
      display: 'grid', gridTemplateColumns: grid, alignItems: 'center',
      padding: '3px 8px', borderRadius: LL.r.md,
      background: done ? `color-mix(in oklab, ${LL.accent} 9%, ${LL.surface})` : LL.surface,
      boxShadow: focus ? `inset 0 0 0 1.5px ${LL.accent}` : 'none',
      transform: dx ? `translateX(${dx}px)` : undefined,
      transition: dx ? 'background-color .25s, box-shadow .2s' : 'background-color .25s, box-shadow .2s, transform .2s',
      '--ll-flash-from': `color-mix(in oklab, ${LL.accent} 30%, ${LL.surface})`,
      animation: flashing ? 'llRowFlash .45s ease-out' : undefined,
    }}>
      {/* Mini-sets hang off their activation set: a thin rail links the group. */}
      {(linkTop || linkBottom) && (
        <span aria-hidden="true" style={{
          position: 'absolute', left: 25, width: 2, borderRadius: LL.r.pill, background: `color-mix(in oklab, ${LL.info} 45%, transparent)`,
          top: linkTop ? 0 : '72%', bottom: linkBottom ? 0 : '72%',
        }} />
      )}
      <button aria-label={mini ? `Mini-set ${label.slice(1)} type` : `Set ${label} type`} onClick={onType} style={{
        position: 'relative', height: 32, width: 30, borderRadius: LL.r.sm, border: 'none', cursor: 'pointer', padding: 0,
        background: type !== 'normal' ? `color-mix(in oklab, ${typeColor} ${mini ? 12 : 18}%, ${LL.surface})` : 'transparent',
        color: type !== 'normal' ? ink(typeColor) : (focus ? LL.accentText : LL.fgMute),
        fontFamily: LL.display, fontSize: label.length > 2 ? LL.fs.meta : LL.fs.body, fontWeight: 700,
      }}>{label}</button>
      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgDim, paddingLeft: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums' }}>
        {mini ? <span style={{ color: ink(LL.info), fontSize: LL.fs.sm }}>{{ myo: 'Myo', restpause: 'Pause', cluster: 'Cluster' }[rx.tech] || 'Mini'}</span> : prevLabel}
      </div>
      {firstCell}
      {secondCell}
      {showRpe && (type === 'warmup' || mini) && <span aria-hidden="true" />}
      {showRpe && type !== 'warmup' && !mini && (() => {
        const v = editField ? editPad?.rpe : rpe;
        const shown = v == null ? '–' : effort !== 'rpe' ? (rirFromRpe(v) >= 4 ? '4+' : String(rirFromRpe(v))) : v;
        return cell(shown, onRpe, effort === 'plain' ? 'Reps left' : effort === 'rir' ? 'Reps in reserve' : 'RPE', editField === 'rpe'
          ? { ...editRing, fontSize: LL.fs.body }
          : { fontSize: v ? LL.fs.body : LL.fs.lg, fontWeight: v ? 700 : 500, color: v ? LL.fg : LL.fgFaint, background: 'transparent', boxShadow: v ? 'none' : `inset 0 0 0 1px ${LL.line}` });
      })()}
      <div style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
        <button aria-label={done ? 'Un-log set' : 'Log set'} onClick={onCheck} style={{
          width: 40, height: 36, borderRadius: LL.r.md, cursor: 'pointer',
          border: done ? 'none' : `1.5px solid ${focus ? LL.accent : LL.line2}`,
          background: done ? LL.accent : 'transparent',
          color: done ? LL.accentInk : (focus ? LL.accentText : LL.fgDim),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span className="ll-anim" style={{ display: 'flex', animation: flashing ? `llCheckPop .32s ${LL.spring}` : undefined }}>
            <LLIcon.check style={{ width: 20, height: 20 }} />
          </span>
        </button>
        {pr && (
          <span className="ll-anim" style={{
            position: 'absolute', top: -6, right: -2, padding: '1px 5px', borderRadius: LL.r.sm,
            background: LL.pr, color: '#1a1200', fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 800, letterSpacing: 0.4,
            animation: `llPrIn .4s ${LL.spring}`,
          }}>PR</span>
        )}
      </div>
    </div>
    </div>
  );
}

// ——— Inline cards inside a slot ———————————————————————————————————————

// Asked once, the first time a bodyweight lift shows up without a known body weight.
function BodyweightAsk({ units, name, onSave, onDismiss }) {
  const [v, setV] = React.useState('');
  const n = parseFloat(String(v).replace(',', '.'));
  const ok = n > (units === 'kg' ? 25 : 55) && n < (units === 'kg' ? 300 : 660);
  return (
    <div style={{ margin: '0 12px 10px', padding: 12, borderRadius: LL.r.md, background: LL.surface2, border: `1px solid ${LL.line}`, animation: reduceMotion ? undefined : 'llFadeUp .24s ' + LL.ease }} className="ll-anim">
      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, color: LL.fg }}>What do you weigh?</div>
      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 2, lineHeight: 1.4 }}>
        {name} counts your body weight in e1RM, volume and records. Saved as today's weigh-in.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <input value={v} onChange={e => setV(e.target.value.replace(/[^\d.,]/g, ''))} inputMode="decimal" aria-label={`Body weight in ${unitLabel(units)}`}
            placeholder={units === 'kg' ? '80' : '180'}
            onKeyDown={e => { if (e.key === 'Enter' && ok) onSave(n); }}
            style={{ width: '100%', boxSizing: 'border-box', height: 44, padding: '0 40px 0 12px', borderRadius: LL.r.sm, border: `1px solid ${LL.line2}`, background: LL.surface, color: LL.fg, fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, outline: 'none' }} />
          <span style={{ position: 'absolute', right: 12, top: 0, bottom: 0, display: 'flex', alignItems: 'center', fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgDim, pointerEvents: 'none' }}>{unitLabel(units)}</span>
        </div>
        <button disabled={!ok} onClick={() => onSave(n)} style={{ height: 44, padding: '0 16px', borderRadius: LL.r.sm, border: 'none', background: ok ? LL.accent : LL.surface3, color: ok ? LL.accentInk : LL.fgDim, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, cursor: ok ? 'pointer' : 'default' }}>Save</button>
        <button onClick={onDismiss} style={{ height: 44, padding: '0 10px', borderRadius: LL.r.sm, border: 'none', background: 'transparent', color: LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, cursor: 'pointer' }}>Not now</button>
      </div>
    </div>
  );
}

// "This hurt last time": the two best like-for-like swaps (same ranking as the
// exercise picker's smart substitutes), or keep the lift.
function JointSwap({ state, slot, onSwap, onKeep }) {
  const hist = historyOf(state.sessions);
  const flag = React.useMemo(() => jointFlagFor(state, slot.exerciseId), [hist, slot.exerciseId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!flag) return null;
  return <JointSwapCard state={state} slot={slot} flag={flag} onSwap={onSwap} onKeep={onKeep} />;
}
function JointSwapCard({ state, slot, flag, onSwap, onKeep }) {
  const { items } = useExerciseIndex();
  const subs = React.useMemo(() => {
    const target = resolveTarget(items, slot.exerciseId) || resolveTarget(items, { id: slot.exerciseId, name: slot.name, primary: [slot.group] });
    return rankSubstitutes(items, target, { profileEquip: state.profile?.equipment, limit: 2 });
  }, [items, slot.exerciseId]); // eslint-disable-line react-hooks/exhaustive-deps
  const when = new Date(flag.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return (
    <div className="ll-anim" style={{
      margin: '0 12px 10px', padding: '10px 12px 12px', borderRadius: LL.r.md,
      background: `color-mix(in oklab, ${LL.warn} 10%, ${LL.surface})`, border: `1px solid color-mix(in oklab, ${LL.warn} 38%, ${LL.line})`,
      animation: reduceMotion ? undefined : 'llFadeUp .24s ' + LL.ease,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span style={{ width: 26, height: 26, borderRadius: LL.r.sm, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `color-mix(in oklab, ${LL.warn} 22%, transparent)`, color: ink(LL.warn) }}>
          <JointIcon style={{ width: 16, height: 16 }} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, color: LL.fg }}>Joint pain here last time</div>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 1 }}>{flag.muscle} · {when}. A similar lift may sit better today.</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
        {subs.map(x => (
          <button key={x.item.id} onClick={() => onSwap(itemToExercise(x.item))} style={{
            height: 34, maxWidth: '100%', padding: '0 12px', borderRadius: LL.r.pill, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            border: `1px solid ${LL.line2}`, background: LL.surface, color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600,
          }}><LLIcon.swap style={{ width: 14, height: 14, flexShrink: 0, color: LL.fgMute }} /><span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.item.name}</span></button>
        ))}
        <button onClick={onKeep} style={{ height: 34, padding: '0 12px', borderRadius: LL.r.pill, cursor: 'pointer', border: 'none', background: 'transparent', color: LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600 }}>Keep it</button>
      </div>
    </div>
  );
}

// One-tap muscle check-in (RP / Alpha-style): pump, soreness from last time,
// joint pain. Every answer saves on tap; nothing blocks logging. Once all
// questions are answered it folds into a one-line receipt.
const FB_Q = [
  ['pump', 'Pump'],
  ['soreness', 'Sore from last time'],
  ['joint', 'Joints'],
];
const FB_SHORT = {
  pump: { low: 'low pump', ok: 'moderate pump', great: 'great pump' },
  soreness: { none: 'not sore', healed: 'healed', sore: 'still sore' },
  joint: { none: 'joints fine', some: 'some joint pain', bad: 'joint pain' },
};
export function MuscleCheck({ muscle, value, askSoreness, exercises, done, onAnswer, onSkip, onOff, embedded = false }) {
  const [open, setOpen] = React.useState(!done);
  React.useEffect(() => { if (done) setOpen(false); }, [done]);
  const v = value || {};
  const qs = FB_Q.filter(([key]) => key !== 'soreness' || askSoreness);
  const receipt = qs.map(([key]) => (v[key] ? FB_SHORT[key][v[key]] : null)).filter(Boolean).join(' · ');
  if (!open) {
    return (
      <div style={{ height: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '0 6px 0 12px', borderRadius: LL.r.md, background: LL.surface2 }}>
        <span style={{ width: 22, height: 22, borderRadius: LL.r.pill, flexShrink: 0, background: LL.accentSoft, color: LL.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LLIcon.check style={{ width: 14, height: 14 }} /></span>
        <span style={{ flex: 1, minWidth: 0, fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <b style={{ color: LL.fg, fontWeight: 600 }}>{muscle}</b> · {receipt}
        </span>
        <button onClick={() => setOpen(true)} style={{ height: 36, padding: '0 10px', border: 'none', background: 'transparent', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
      </div>
    );
  }
  const bad = v.joint === 'bad' || v.joint === 'some';
  const hurt = Array.isArray(v.jointEx) && v.jointEx.length ? v.jointEx : exercises.map(e => e.id);
  return (
    <div role="group" aria-label={`${muscle} check-in`} className="ll-anim" style={{
      padding: embedded ? 0 : '12px 12px 10px', borderRadius: LL.r.md,
      background: embedded ? 'transparent' : LL.surface2, border: embedded ? 'none' : `1px solid ${LL.line}`,
      animation: reduceMotion || embedded ? undefined : 'llFadeUp .24s ' + LL.ease,
    }}>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: LL.fgDim }}>Check-in</span>
          <span style={{ flex: 1, minWidth: 0, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, color: LL.fg }}>{muscle}</span>
          <button onClick={onSkip} style={{ height: 30, padding: '0 10px', borderRadius: LL.r.pill, border: `1px solid ${LL.line2}`, background: 'transparent', color: LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, cursor: 'pointer' }}>Skip</button>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {qs.map(([key, q]) => (
          <div key={key} style={{ display: 'grid', gridTemplateColumns: '78px 1fr', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, color: LL.fgMute, lineHeight: 1.2 }}>{q}</span>
            <div role="radiogroup" aria-label={`${muscle}: ${q}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 4 }}>
              {FEEDBACK_OPTIONS[key].map(([id, label]) => {
                const on = v[key] === id;
                const tone = key === 'joint' && id === 'bad' ? LL.danger : key === 'joint' && id === 'some' ? LL.warn : key === 'soreness' && id === 'sore' ? LL.warn : null;
                return (
                  <button key={id} role="radio" aria-checked={on} onClick={() => onAnswer({ [key]: on ? null : id })} style={{
                    height: 34, minWidth: 0, padding: '0 4px', borderRadius: LL.r.sm, cursor: 'pointer',
                    border: `1px solid ${on ? (tone || LL.accent) : LL.line}`,
                    background: on ? `color-mix(in oklab, ${tone || LL.accent} 16%, ${LL.surface})` : LL.surface,
                    color: on ? (tone ? ink(tone) : LL.accentText) : LL.fg,
                    fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    transition: `background .18s ${LL.ease}, border-color .18s ${LL.ease}`,
                  }}>{label}</button>
                );
              })}
            </div>
          </div>
        ))}
        {bad && exercises.length > 1 && (
          <div style={{ display: 'grid', gridTemplateColumns: '78px 1fr', alignItems: 'start', gap: 8, marginTop: 2 }}>
            <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, color: LL.fgMute, paddingTop: 7 }}>Which one?</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {exercises.map(e => {
                const on = hurt.includes(e.id);
                return (
                  <button key={e.id} aria-pressed={on} onClick={() => {
                    const next = on ? hurt.filter(id => id !== e.id) : [...hurt, e.id];
                    onAnswer({ jointEx: next.length ? next : hurt });
                  }} style={{
                    height: 30, maxWidth: '100%', padding: '0 10px', borderRadius: LL.r.pill, cursor: 'pointer',
                    border: `1px solid ${on ? LL.warn : LL.line}`, background: on ? `color-mix(in oklab, ${LL.warn} 14%, ${LL.surface})` : LL.surface,
                    color: on ? ink(LL.warn) : LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{e.name}</button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {!embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
          <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, color: LL.fgDim }}>Tunes next week's sets for {muscle.toLowerCase()}.</span>
          <button onClick={onOff} style={{ height: 30, padding: 0, border: 'none', background: 'transparent', color: LL.fgDim, fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}>Don't ask</button>
        </div>
      )}
    </div>
  );
}

// Finish sheet: one card that pages through the muscles still open (fixed
// height, so the Finish button never moves). Answering all three advances.
export function FinishCheckIn({ muscles, feedback, soreAsk, exercisesOf, onAnswer, onSkip, onOff, bare = false }) {
  const [i, setI] = React.useState(0);
  const list = muscles;
  const cur = list[Math.min(i, list.length - 1)];
  if (!cur) return null;
  const answer = (patch) => {
    onAnswer(cur.muscle, patch);
    const next = { ...(feedback[cur.muscle] || {}), ...patch };
    const complete = next.pump != null && next.joint != null && (next.soreness != null || !soreAsk.has(cur.muscle));
    if (complete && !(next.joint === 'bad' && exercisesOf(cur).length > 1)) setTimeout(() => setI(x => Math.min(x + 1, list.length - 1)), 260);
  };
  return (
    <div style={bare ? {} : { marginTop: 12, padding: '12px 12px 10px', borderRadius: LL.r.md, background: LL.surface2, border: `1px solid ${LL.line}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: LL.fgDim }}>Check-in</span>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 4, overflowX: 'auto' }}>
          {list.map((m, k) => {
            const on = m.muscle === cur.muscle;
            return (
              <button key={m.muscle} onClick={() => setI(k)} aria-pressed={on} style={{
                height: 28, padding: '0 10px', borderRadius: LL.r.pill, flexShrink: 0, cursor: 'pointer',
                border: `1px solid ${on ? LL.accent : 'transparent'}`, background: on ? LL.accentSoft : LL.surface,
                color: on ? LL.accentText : LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600,
              }}>{m.muscle}</button>
            );
          })}
        </div>
        <button onClick={() => { onSkip(cur.muscle); setI(x => Math.max(0, Math.min(x, list.length - 2))); }} style={{ height: 28, padding: '0 10px', borderRadius: LL.r.pill, border: `1px solid ${LL.line2}`, background: 'transparent', color: LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>Skip</button>
      </div>
      <MuscleCheck embedded muscle={cur.muscle} value={feedback[cur.muscle]} askSoreness={soreAsk.has(cur.muscle)} exercises={exercisesOf(cur)} done={false}
        onAnswer={answer} onSkip={() => onSkip(cur.muscle)} onOff={onOff} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
        <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, color: LL.fgDim }}>Optional. Tunes next week's set counts.</span>
        <button onClick={onOff} style={{ height: 30, padding: 0, border: 'none', background: 'transparent', color: LL.fgDim, fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}>Don't ask</button>
      </div>
    </div>
  );
}

// Inset-group row with a control on the right (options sheet).
function SettingRow({ icon, label, sub, onClick, children }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 12, minHeight: 56, padding: '8px 14px', textAlign: 'left', boxSizing: 'border-box',
      background: 'transparent', border: 'none', borderTop: `1px solid ${LL.line}`, width: '100%', cursor: onClick ? 'pointer' : 'default',
    }}>
      <span style={{
        width: 32, height: 32, borderRadius: LL.r.sm, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `color-mix(in oklab, ${LL.accent} 14%, ${LL.surface2})`, color: LL.accentText,
      }}>{React.createElement(icon, { style: { width: 18, height: 18 } })}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 600, color: LL.fg }}>{label}</div>
        {sub && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 1 }}>{sub}</div>}
      </span>
      {children}
    </Tag>
  );
}
function Segmented({ value, options, onChange, label }) {
  return (
    <div role="radiogroup" aria-label={label} style={{ display: 'flex', padding: 3, gap: 2, borderRadius: LL.r.sm, background: LL.surface3, flexShrink: 0 }}>
      {options.map(([id, l]) => {
        const on = value === id;
        return (
          <button key={id} role="radio" aria-checked={on} onClick={(e) => { e.stopPropagation(); onChange(id); }} style={{
            height: 30, minWidth: 48, padding: '0 10px', borderRadius: LL.r.sm, border: 'none', cursor: 'pointer',
            background: on ? LL.surface : 'transparent', color: on ? LL.fg : LL.fgMute, boxShadow: on ? LL.shadow.sm : 'none',
            fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: on ? 700 : 600,
          }}>{l}</button>
        );
      })}
    </div>
  );
}
function GaugeIcon(p) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}>
      <path d="M3.5 13.5a6.5 6.5 0 1113 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M10 13.5l3-4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="10" cy="13.5" r="1.3" fill="currentColor" />
    </svg>
  );
}
function JointIcon(p) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}>
      <circle cx="10" cy="10" r="2.6" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8.2 8.2L4 4M11.8 11.8L16 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M13.5 4.5l1 1M5.5 13.5l1 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// ——— Inline keypad ———————————————————————————————————————————————————
// Weight · Reps · RPE in one surface: the RPE tab swaps the digits for the
// effort grid at the same key height, with the same Log key. Cardio rows edit
// distance · time; timed holds get a stopwatch / countdown in place of the digits.
const RPE_VALUES = [6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10];
const RIR_LABEL = { 6: '4+ left', 6.5: '3–4 left', 7: '3 left', 7.5: '2–3 left', 8: '2 left', 8.5: '1–2 left', 9: '1 left', 9.5: 'maybe 1', 10: 'max effort' };

function KeypadDock({ pad, slot, units, kind, effort, bwNow, sound, haptics, rest, restLeft, onRest, onChange, onClose, onLog, onPlates }) {
  useSheetHistory(true, onClose); // Android Back closes the keypad first
  const { state, runToastAction } = useApp();
  const { showEffort } = useSimple(); // simple mode: effort tab only when opted in
  const [restOpen, setRestOpen] = React.useState(false);
  const [tick, setTick] = React.useState(0);
  const timer = pad.timer || null;
  React.useEffect(() => {
    if (!timer || !timer.running) return;
    const t = setInterval(() => setTick(x => x + 1), 200);
    return () => clearInterval(t);
  }, [timer?.running, timer?.startedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const elapsed = timer ? Math.max(0, Math.floor(((timer.running ? Date.now() : timer.stoppedAt || Date.now()) - timer.startedAt) / 1000)) : 0;
  // Countdown finished: chime, stop, fill in the target.
  React.useEffect(() => {
    if (!timer || !timer.running || timer.mode !== 'down') return;
    if (elapsed >= timer.target) {
      try { playRestBeep({ sound }); } catch {}
      if (haptics) tryVibrate([60, 40, 60]);
      onLog({ rText: String(timer.target), timer: null });
    }
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!slot) return null;
  void tick;
  const k = kind || { load: 'weighted', track: 'reps' };
  const KGF = 0.45359237;
  const cardio = !!pad.cardio;
  const timed = !cardio && slot.unit === 'sec';
  const bwLoad = !cardio && isBwLoad(k.load);
  const field = pad.field;
  const isW = field === 'w';
  const isDist = field === 'dist';
  const isDur = field === 'dur';
  const isRpe = field === 'rpe';
  const decimal = isW || isDist;
  const text = isW ? pad.wText : isDist ? pad.dText : isDur ? pad.tDigits : pad.rText;
  // Any edit clears the inline error. `fresh` means the next digit replaces the
  // whole value (just focused, or just stepped with − / +).
  const set = (t, fresh = false) => onChange(isW ? { wText: t, fresh, err: null } : isDist ? { dText: t, fresh, err: null } : isDur ? { tDigits: t, fresh, err: null } : { rText: t, fresh, err: null });
  const press = (k2) => {
    if (haptics) tryVibrate(6);
    if (k2 === 'del') { set(pad.fresh ? '' : String(text || '').slice(0, -1)); return; }
    if (k2 === '.') { if (!decimal) return; const base = pad.fresh ? '0' : (text || '0'); if (!String(base).includes('.')) set(base + '.'); return; }
    const next = pad.fresh ? k2 : String(text || '') + k2;
    if (isDur) { if (next.replace(/^0+/, '').length > 6) return; set(next.replace(/^0+(\d)/, '$1')); return; }
    if (!decimal && next.length > (timed ? 4 : 3)) return;
    if (decimal && next.replace('.', '').length > 5) return;
    set(next.replace(/^0(\d)/, '$1'));
  };
  const step = (dir) => {
    if (haptics) tryVibrate(8);
    if (isDur) { const s = Math.max(0, digitsSecs(text) + dir * 30); set(s ? durDigits(s) : '', true); return; }
    if (isDist) { const v = Math.max(0, (parseFloat(text) || 0) + dir * 0.1); set(v ? String(+v.toFixed(2)) : '', true); return; }
    if (!isW) { const r = Math.max(0, (parseInt(text, 10) || 0) + dir * (timed ? 5 : 1)); set(String(r), true); return; }
    const stLb = stepFor(slot, units);
    const st = units === 'kg' ? Math.round(stLb * KGF * 100) / 100 : stLb;
    const v = parseFloat(text) || 0, q = v / st;
    const n = dir > 0 ? (Math.floor(q + 1e-3) + 1) * st : Math.max(0, (Math.ceil(q - 1e-3) - 1) * st);
    set(String(Math.round(n * 100) / 100), true);
  };
  const key = (label, onClick, extra = {}, aria) => (
    <button type="button" onClick={onClick} aria-label={aria} style={{
      height: 50, borderRadius: LL.r.sm, border: 'none', cursor: 'pointer', background: LL.surface2, color: LL.fg,
      fontFamily: LL.display, fontSize: LL.fs.stat, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
      touchAction: 'manipulation', padding: 0, ...extra,
    }}>{label}</button>
  );
  const errField = pad.err ? pad.errField : null;
  const fieldBtn = (f, label, value, unit, aria) => {
    const on = field === f;
    const bad = errField === f;
    return (
      <button type="button" onClick={() => onChange({ field: f, fresh: true, timer: timer && timer.running ? timer : null })} aria-pressed={on} aria-label={aria} style={{
        flex: f === 'rpe' ? 0.7 : 1, minWidth: 0, height: 52, borderRadius: LL.r.md, cursor: 'pointer', padding: '0 12px', textAlign: 'left',
        border: `1.5px solid ${bad ? LL.danger : on ? LL.accent : LL.line}`, background: on ? LL.accentSoft : LL.surface2,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
      }}>
        <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, color: bad ? LL.danger : on ? LL.accentText : LL.fgDim, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        <span style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: value ? LL.fg : LL.fgDim, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value || '—'}{unit && value ? <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgDim, fontWeight: 500 }}> {unit}</span> : null}</span>
      </button>
    );
  };
  const rowType = slot.rows[pad.row]?.type;
  const setNo = rowType === 'warmup' ? 'Warm-up' : rowType === 'mini' ? `Mini-set ${miniNumber(slot, pad.row)}` : cardio ? (slot.rows.filter(isMainRow).length > 1 ? `Interval ${workIndexOf(slot, pad.row) + 1}` : 'Cardio') : `Set ${workIndexOf(slot, pad.row) + 1}`;
  const repsLabel = timed ? 'Time' : pad.amrap ? 'Reps · max' : 'Reps';
  const effortLabel = effort === 'plain' ? 'Left' : effort === 'rir' ? 'RIR' : 'RPE';
  const effortValue = pad.rpe == null ? '' : effort !== 'rpe' ? (rirFromRpe(pad.rpe) >= 4 ? '4+' : String(rirFromRpe(pad.rpe))) : String(pad.rpe);
  const logKey = key(<><LLIcon.check style={{ width: 20, height: 20 }} />&nbsp;{pad.logged ? 'Save' : 'Log'}</>, () => onLog(),
    { background: LL.accent, color: LL.accentInk, fontFamily: LL.ui, fontSize: LL.fs.btn }, pad.logged ? `Save ${setNo}` : `Log ${setNo}`);
  const wLb = (() => { const v = parseFloat(String(pad.wText || '').replace(',', '.')); return v > 0 ? (units === 'kg' ? v / KGF : v) : 0; })();

  // Hint line: an error, the latest toast (toasts live here while the keypad is
  // open, so they never float over the next card), or what you're logging.
  const live = state.toasts[state.toasts.length - 1] || null;
  let hint;
  if (pad.err) hint = <span role="alert" style={{ color: LL.danger, fontWeight: 600 }}>{pad.err}</span>;
  else if (live) {
    hint = (
      <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%', color: live.tone === 'pr' ? LL.prText : LL.fg, fontWeight: 600 }}>
        {live.tone === 'pr' && <LLIcon.trophy style={{ width: 14, height: 14, flexShrink: 0 }} />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{live.text}</span>
        {live.action && <button type="button" onClick={() => runToastAction(live.id)} style={{ height: 26, padding: '0 10px', borderRadius: LL.r.pill, border: 'none', background: LL.accentSoft, color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>{live.action}</button>}
      </span>
    );
  } else if (cardio && !isRpe) {
    const d = metersFrom(parseFloat(String(pad.dText || '').replace(',', '.')), units), s = digitsSecs(pad.tDigits);
    const p = paceSecPerKm(d, s);
    hint = <><b style={{ color: LL.fg }}>{setNo}</b> · {p ? `${fmtPace(p, units)} pace` : slot.variant?.name || slot.name}</>;
  } else if (bwLoad && isW && bwNow > 0) {
    const add = parseFloat(String(pad.wText || '').replace(',', '.')) || 0;
    const addLb = units === 'kg' ? add / KGF : add;
    const eff = effectiveLoad(addLb, k.load, bwNow);
    hint = <><b style={{ color: LL.fg }}>{setNo}</b> · BW {fmtWhole(bwNow, units)} {k.load === 'assisted' ? '−' : '+'} {fmtWeight(addLb, units)} = <b style={{ color: LL.fg }}>{fmtWhole(eff, units)} {unitLabel(units)}</b></>;
  } else if (pad.amrap && !isRpe) hint = <><b style={{ color: LL.fg }}>{setNo}</b> · as many reps as you can</>;
  else if (rowType === 'mini') hint = <><b style={{ color: LL.fg }}>{setNo}</b> · {TECHNIQUES[slot.rows[pad.row]?.tech]?.label || 'Mini-set'} · {slot.variant?.name || slot.name}</>;
  else hint = <><b style={{ color: LL.fg }}>{setNo}</b> · {slot.variant?.name || slot.name}</>;

  // ——— timed-hold timer ———
  const target = Math.max(5, parseInt(pad.rText, 10) || slot.repMax || 30);
  const startTimer = (mode) => { if (haptics) tryVibrate(10); unlockAudioContext(); onChange({ field: 'reps', timer: { mode, target, startedAt: Date.now(), running: true }, err: null }); };
  const stopTimer = () => {
    if (!timer) return;
    if (haptics) tryVibrate(10);
    const secs = Math.max(1, timer.mode === 'down' ? Math.min(timer.target, elapsed) : elapsed);
    onLog({ rText: String(secs), timer: null });
  };
  const timerShown = timed && field === 'reps' && timer;
  const clock = timer ? (timer.mode === 'down' ? Math.max(0, timer.target - elapsed) : elapsed) : 0;
  const ringPct = timer && timer.mode === 'down' ? Math.min(1, elapsed / timer.target) : null;

  return (
    <div role="group" aria-label="Number keypad" data-ll-sheet="open" data-ll-quiet-toasts="keypad" className="ll-anim" style={{
      position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 30,
      background: LL.surface, borderTop: `1px solid ${LL.line2}`, borderTopLeftRadius: LL.r.lg, borderTopRightRadius: LL.r.lg,
      boxShadow: LL.shadow.sheet, padding: '10px 12px calc(env(safe-area-inset-bottom, 0px) + 10px)',
      animation: `llSlideUp .22s ${LL.ease}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, minHeight: 32 }}>
        {restOpen && rest ? (
          <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 6 }}>
            {[['−15', () => onRest(-15)], ['+15', () => onRest(15)], ['Skip', () => { onRest('skip'); setRestOpen(false); }]].map(([l, fn]) => (
              <button key={l} type="button" onClick={fn} style={{ height: 32, padding: '0 12px', borderRadius: LL.r.pill, border: `1px solid ${LL.line2}`, background: l === 'Skip' ? LL.accent : 'transparent', color: l === 'Skip' ? LL.accentInk : LL.fg, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 700, cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}>{l}</button>
            ))}
          </div>
        ) : (
          <div style={{ flex: 1, minWidth: 0, fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{hint}</div>
        )}
        {rest && restLeft > 0 && (
          <button type="button" onClick={() => setRestOpen(v => !v)} aria-label={`Rest ${fmtClock(restLeft)} left${rest.label ? `, next ${rest.label}` : ''}. Adjust`} aria-expanded={restOpen} style={{
            height: 32, padding: '0 10px', borderRadius: LL.r.pill, border: 'none', cursor: 'pointer', flexShrink: 0,
            background: LL.accentSoft, color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}><LLIcon.timer style={{ width: 14, height: 14 }} />{fmtClock(restLeft)}</button>
        )}
        {onPlates && wLb > 0 && !restOpen && (
          <button type="button" onClick={() => onPlates(wLb)} style={{ height: 32, padding: '0 12px', borderRadius: LL.r.pill, border: `1px solid ${LL.line2}`, background: 'transparent', color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>Plates</button>
        )}
        <button type="button" aria-label="Close keypad" onClick={onClose} style={{ width: 44, height: 44, margin: -6, border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer', flexShrink: 0 }}>
          <span style={{ width: 32, height: 32, borderRadius: LL.r.pill, background: LL.surface2, color: LL.fgMute, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LLIcon.chevDown style={{ width: 18, height: 18 }} /></span>
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        {cardio ? (
          <>
            {fieldBtn('dist', 'Distance', pad.dText, distUnit(units), 'Distance field')}
            {fieldBtn('dur', 'Time', fmtDigits(pad.tDigits), null, 'Time field')}
          </>
        ) : (
          <>
            {fieldBtn('w', bwLoad ? (k.load === 'assisted' ? 'Assistance' : 'Added') : 'Weight', bwLoad && !(parseFloat(pad.wText) > 0) ? 'BW' : pad.wText, bwLoad && !(parseFloat(pad.wText) > 0) ? null : unitLabel(units), bwLoad ? (k.load === 'assisted' ? 'Assistance field' : 'Added weight field') : 'Weight field')}
            {fieldBtn('reps', repsLabel, timed ? (pad.rText ? fmtDuration(+pad.rText) : '') : pad.rText, null, `${timed ? 'Seconds' : 'Reps'} field`)}
          </>
        )}
        {showEffort && fieldBtn('rpe', effortLabel, effortValue, null, `${effort === 'plain' ? 'Reps left' : effort === 'rir' ? 'Reps in reserve' : 'RPE'} field`)}
      </div>
      {isRpe ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.1fr', gridTemplateRows: 'repeat(4, 50px)', gap: 6 }}>
          {RPE_VALUES.map((v, i) => {
            const on = pad.rpe === v;
            const big = effort !== 'rpe' ? (v <= 6 ? '4+' : String(rirFromRpe(v))) : String(v);
            const sub = effort === 'plain' ? (v >= 10 ? 'all-out' : 'reps left') : effort === 'rir' ? `RPE ${v}` : RIR_LABEL[v];
            return (
              <React.Fragment key={v}>
                {key(
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.05 }}>
                    <span>{big}</span>
                    <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 500, color: on ? LL.accentText : LL.fgDim }}>{sub}</span>
                  </span>,
                  () => { if (haptics) tryVibrate(6); onChange({ rpe: on ? null : v, err: null }); },
                  { fontSize: LL.fs.lg, background: on ? LL.accentSoft : LL.surface2, color: on ? LL.accentText : LL.fg, boxShadow: on ? `inset 0 0 0 1.5px ${LL.accent}` : 'none', gridColumn: (i % 3) + 1, gridRow: Math.floor(i / 3) + 1 },
                  effort === 'plain' ? `${big} rep${big === '1' ? '' : 's'} left` : effort === 'rir' ? `${big} rep${big === 1 || big === '1' ? '' : 's'} in reserve (RPE ${v})` : `RPE ${v}, ${RIR_LABEL[v]}`,
                )}
              </React.Fragment>
            );
          })}
          <div style={{ gridColumn: 4, gridRow: '1 / span 3', borderRadius: LL.r.sm, background: LL.surface2, padding: '10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, color: LL.fgDim, textTransform: 'uppercase' }}>{effort === 'plain' ? 'Reps left' : effort === 'rir' ? 'In reserve' : 'Effort'}<Explain id={effort === 'rpe' ? 'effort' : 'rir'} size={14} /></span>
            <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, lineHeight: 1.35 }}>{effort === 'rir' ? 'Optional. Reps you could still have done.' : 'Optional. Reps you had left in the tank.'}</span>
          </div>
          {key('Clear', () => onChange({ rpe: null }), { gridColumn: '1 / span 3', gridRow: 4, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: pad.rpe == null ? LL.fgDim : LL.fg }, `Clear ${effortLabel}`)}
          <div style={{ gridColumn: 4, gridRow: 4, display: 'grid' }}>{logKey}</div>
        </div>
      ) : timerShown ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.1fr', gridTemplateRows: 'repeat(4, 50px)', gap: 6 }}>
          <div style={{ gridColumn: '1 / span 3', gridRow: '1 / span 3', borderRadius: LL.r.sm, background: LL.surface2, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {ringPct != null && <div aria-hidden="true" style={{ position: 'absolute', left: 0, bottom: 0, height: 4, width: `${ringPct * 100}%`, background: LL.accent, transition: 'width .2s linear' }} />}
            <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, color: LL.fgDim, textTransform: 'uppercase' }}>
              {timer.mode === 'down' ? `Hold · target ${fmtDuration(timer.target)}` : 'Hold · stopwatch'}
            </span>
            <span aria-live="off" style={{ fontFamily: LL.display, fontSize: LL.fs.hero, fontWeight: 700, color: timer.running ? LL.fg : LL.accentText, fontVariantNumeric: 'tabular-nums', lineHeight: 1.05 }}>{fmtDuration(clock)}</span>
            <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute }}>{timer.mode === 'down' ? 'Logs itself when time is up' : 'Tap Done when you break'}</span>
          </div>
          {key(<span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}><span>Done</span><span style={{ fontSize: LL.fs.cap, fontWeight: 600, opacity: 0.8 }}>logs {fmtDuration(Math.max(1, timer.mode === 'down' ? Math.min(timer.target, elapsed) : elapsed))}</span></span>, stopTimer, { gridColumn: 4, gridRow: '1 / span 2', height: 'auto', fontFamily: LL.ui, fontSize: LL.fs.btn, background: LL.accent, color: LL.accentInk }, 'Done: log the time held')}
          {key(timer.mode === 'down' ? 'Count up' : 'Countdown', () => startTimer(timer.mode === 'down' ? 'up' : 'down'), { gridColumn: 4, gridRow: 3, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, background: LL.surface3 }, 'Switch timer mode')}
          {key('Cancel timer', () => onChange({ timer: null, fresh: true }), { gridColumn: '1 / span 3', gridRow: 4, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fgMute }, 'Cancel the timer and type the time')}
          <div style={{ gridColumn: 4, gridRow: 4, display: 'grid' }}>{key(<span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1 }}><span>Log {fmtDuration(+pad.rText || 0)}</span><span style={{ fontSize: LL.fs.cap, fontWeight: 600, color: LL.fgMute }}>instead</span></span>, () => onLog({ timer: null }), { fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, background: LL.surface3 }, 'Log the planned time instead')}</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.1fr', gap: 6 }}>
          {key('1', () => press('1'))}{key('2', () => press('2'))}{key('3', () => press('3'))}
          {key(<LLIcon.minus />, () => step(-1), { background: LL.surface3, fontSize: LL.fs.body }, isW ? (bwLoad ? 'Less' : 'Decrease weight') : isDur ? 'Minus 30 seconds' : isDist ? 'Minus 0.1' : timed ? 'Minus 5 seconds' : 'One less')}
          {key('4', () => press('4'))}{key('5', () => press('5'))}{key('6', () => press('6'))}
          {key(<LLIcon.plus />, () => step(1), { background: LL.surface3, fontSize: LL.fs.body }, isW ? (bwLoad ? 'More' : 'Increase weight') : isDur ? 'Plus 30 seconds' : isDist ? 'Plus 0.1' : timed ? 'Plus 5 seconds' : 'One more')}
          {key('7', () => press('7'))}{key('8', () => press('8'))}{key('9', () => press('9'))}
          {cardio
            ? key(isDist ? 'Time' : 'Distance', () => onChange({ field: isDist ? 'dur' : 'dist', fresh: true }), { background: LL.surface3, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600 }, isDist ? 'Next: time' : 'Back to distance')
            : key(isW ? (timed ? 'Time' : 'Reps') : (bwLoad ? 'Added' : 'Weight'), () => onChange({ field: isW ? 'reps' : 'w', fresh: true }), { background: LL.surface3, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600 }, isW ? `Next: ${timed ? 'time' : 'reps'}` : 'Back to weight')}
          {decimal ? key('.', () => press('.'), {}, 'Decimal point')
            : timed ? key(<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><LLIcon.timer style={{ width: 18, height: 18 }} />Timer</span>, () => startTimer('down'), { fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, color: LL.accentText }, 'Start hold timer')
            : <span aria-hidden="true" />}
          {key('0', () => press('0'))}
          {key(<LLIcon.back style={{ width: 22, height: 22 }} />, () => press('del'), {}, 'Backspace')}
          {logKey}
        </div>
      )}
    </div>
  );
}

// ——— Rest bar ————————————————————————————————————————————————————————

function RestBar({ rest, left, onAdjust, onSkip, onReset }) {
  if (!rest) return null;
  const pct = rest.total > 0 ? Math.max(0, Math.min(1, left / rest.total)) : 0;
  const pill = {
    height: 40, minWidth: 54, padding: '0 12px', borderRadius: LL.r.md, border: `1px solid ${LL.line2}`,
    background: 'transparent', color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, cursor: 'pointer',
    fontVariantNumeric: 'tabular-nums',
  };
  return (
    <div className="ll-anim" style={{
      position: 'absolute', left: 10, right: 10, bottom: 'calc(env(safe-area-inset-bottom, 0px) + 10px)', zIndex: 20,
      background: LL.surface2, border: `1px solid ${LL.line2}`, borderRadius: LL.r.lg,
      boxShadow: LL.shadow.lift, overflow: 'hidden',
      animation: `llSlideUp .28s ${LL.ease}`,
    }}>
      <div style={{ height: 4, background: LL.surface3 }}>
        <div style={{ height: '100%', width: `${pct * 100}%`, background: LL.accent, transition: 'width 1s linear' }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, color: LL.fgDim, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            Rest{rest.adapted ? ' · your pace' : ''}
            {rest.adapted && rest.prescribed > rest.total && (
              <button onClick={onReset} style={{ padding: '2px 8px', borderRadius: LL.r.pill, border: `1px solid ${LL.line2}`, background: 'transparent', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 0, textTransform: 'none', cursor: 'pointer' }}>Use {fmtClock(rest.prescribed)}</button>
            )}
          </div>
          <div style={{ fontFamily: LL.display, fontSize: LL.fs.h1, fontWeight: 700, color: LL.fg, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>{fmtClock(left)}</div>
        </div>
        <button style={pill} onClick={() => onAdjust(-15)} aria-label="Subtract 15 seconds">−15</button>
        <button style={pill} onClick={() => onAdjust(15)} aria-label="Add 15 seconds">+15</button>
        <button style={{ ...pill, background: LL.accent, color: LL.accentInk, border: 'none' }} onClick={onSkip}>Skip</button>
      </div>
      {rest.label && (
        <div style={{ padding: '0 12px 10px', marginTop: -4, fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Next · <span style={{ color: LL.fg, fontWeight: 600 }}>{rest.label}</span>
        </div>
      )}
    </div>
  );
}

// ——— Sheets ——————————————————————————————————————————————————————————


function SetTypeSheet({ open, current, tech, cardio, onClose, onPick, onDelete }) {
  const basic = [
    ['normal', 'Working set', 'Counts toward volume, PRs and progression'],
    ['warmup', 'Warm-up', 'Excluded from volume, PRs and progression'],
    ['drop', 'Drop set', 'Reduced weight straight after the previous set'],
    ['failure', 'To failure', 'Taken to technical failure'],
  ];
  const techs = Object.entries(TECHNIQUES).map(([id, t]) => [id, t.label, t.sub]);
  const isMini = current === 'mini';
  const tile = (id, label, sub, tone) => {
    const on = current === id;
    const c = tone || LL.accent;
    return (
      <button key={id} onClick={() => onPick(id)} aria-pressed={on} style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: LL.r.md, cursor: 'pointer', textAlign: 'left',
        background: on ? `color-mix(in oklab, ${c} 14%, ${LL.surface2})` : LL.surface2, border: `1px solid ${on ? c : LL.line}`,
      }}>
        <span style={{
          width: 32, height: 32, borderRadius: LL.r.sm, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: on ? `color-mix(in oklab, ${c} 22%, transparent)` : LL.surface, color: on ? (tone ? ink(tone) : LL.accentText) : LL.fgMute,
          fontFamily: LL.display, fontSize: LL.fs.body, fontWeight: 700,
        }}>{SET_TYPES[id].short || '1'}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{label}</div>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgDim, marginTop: 1, lineHeight: 1.35 }}>{sub}</div>
        </span>
      </button>
    );
  };
  return (
    <LLSheet open={open} onClose={onClose} title={isMini ? 'Mini-set' : 'Set type'} subtitle={isMini && TECHNIQUES[tech] ? `Part of a ${TECHNIQUES[tech].label.toLowerCase()} set · counts as ${tech === 'cluster' ? 'part of one set' : 'half a hard set'}` : undefined} maxHeight="88%">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(isMini ? [['normal', 'Make it a working set', 'Its own set: counts toward PRs and progression']] : cardio ? basic.slice(0, 2) : basic).map(([id, label, sub]) => tile(id, label, sub))}
        {!isMini && !cardio && (
          <>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: LL.fgDim, margin: '10px 2px 2px' }}>Intensity techniques</div>
            {techs.map(([id, label, sub]) => tile(id, label, sub, LL.info))}
          </>
        )}
        <button onClick={onDelete} style={{
          marginTop: 6, height: 48, borderRadius: LL.r.md, border: `1px solid ${LL.line}`, background: 'transparent',
          color: LL.danger, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer',
        }}>{isMini ? 'Delete mini-set' : 'Delete set'}</button>
      </div>
    </LLSheet>
  );
}

// What the form sheet shows for a slot: the alternate in use (matched by name),
// else the exercise, plus any demo link the user saved on it.
function formFor(slot, exById = {}) {
  const v = slot.variant;
  return { exerciseId: v ? null : slot.exerciseId, name: v?.name || slot.name, link: v ? v.yt || '' : slot.yt || exById[slot.exerciseId]?.yt || '' };
}

function SlotMenu({ open, slot, index, count, hasNext, equipment, onClose, onAction }) {
  if (!slot) return <LLSheet open={false} onClose={onClose} />;
  const demo = demoFor(formFor(slot));
  const item = (id, label, sub, Icon, tone, arg) => (
    <MenuItem key={id + (arg?.name || '')} icon={Icon} label={label} sub={sub} tone={tone} onClick={() => onAction(id, arg)} />
  );
  const group = (children) => <MenuGroup>{children}</MenuGroup>;
  return (
    <LLSheet open={open} onClose={onClose} title={slot.name} subtitle={`Exercise ${index + 1} of ${count}`} maxHeight="88%">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {group(<>
          {(slot.alternates || []).map(a => item('alt', `Use ${a.name}`, 'Program alternate', LLIcon.swap, null, a))}
          {slot.variant && item('alt', `Back to ${slot.name}`, null, LLIcon.swap, null, null)}
          {item('swap', 'Replace exercise', 'Logged sets stay put', LLIcon.swap)}
          {item('warmup', 'Add warm-up sets', 'Ramp from your working weight', LLIcon.flame)}
          {usesPlates(equipment) && item('plates', 'Plate calculator', 'What to load on each side', LLIcon.dumbbell)}
          {hasNext && !slot.supersetId && item('superset', 'Superset with next', 'Alternate sets, rest after each round', LLIcon.link)}
          {slot.supersetId && item('unsuperset', 'Remove from superset', null, LLIcon.link)}
          {item('rest', 'Rest time', `Currently ${fmtClock(slot.rest)}`, LLIcon.timer)}
          {item('note', 'Exercise note', 'Seat height, grip, cues', LLIcon.note)}
        </>)}
        {group(<>
          {item('detail', 'History & records', null, LLIcon.chart)}
          {item('yt', demo ? 'Form video' : 'Form guide', demo ? (demo.own ? 'Your linked demo' : `Demo by ${demo.channel}`) : 'Start and end photos, key steps', LLIcon.video)}
          {count > 1 && item('reorder', 'Reorder exercises', 'Drag to change the order', LLIcon.grip)}
          {index > 0 && item('up', 'Move up', null, LLIcon.chevUp)}
          {index < count - 1 && item('down', 'Move down', null, LLIcon.chevDown)}
        </>)}
        {group(item('remove', 'Remove exercise', 'You can undo this', LLIcon.trash, 'danger'))}
      </div>
    </LLSheet>
  );
}

// Drag-to-reorder the whole workout (long lists: one drag instead of many "Move up").
function ReorderSheet({ open, slots, onClose, onApply }) {
  const ROW = 60;
  const [order, setOrder] = React.useState([]);
  const [drag, setDrag] = React.useState(null); // { id, from, startY, dy }
  React.useEffect(() => { if (open) { setOrder(slots.map(s => s.id)); setDrag(null); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const byId = Object.fromEntries(slots.map(s => [s.id, s]));
  const to = drag ? Math.max(0, Math.min(order.length - 1, drag.from + Math.round(drag.dy / ROW))) : -1;
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= order.length) return; const c = [...order]; [c[i], c[j]] = [c[j], c[i]]; setOrder(c); };
  const onDown = (e, id) => {
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    setDrag({ id, from: order.indexOf(id), startY: e.clientY, dy: 0 });
  };
  const onMove = (e) => { if (drag) setDrag(d => d && ({ ...d, dy: e.clientY - d.startY })); };
  const onUp = () => {
    if (!drag) return;
    const c = [...order]; c.splice(drag.from, 1); c.splice(to, 0, drag.id);
    setOrder(c); setDrag(null);
  };
  return (
    <LLSheet open={open} onClose={onClose} title="Reorder exercises" subtitle="Drag the handle" maxHeight="88%">
      <div style={{ position: 'relative' }}>
        {order.map((id, i) => {
          const sl = byId[id]; if (!sl) return null;
          let shift = 0;
          if (drag && id !== drag.id) {
            if (drag.from < to && i > drag.from && i <= to) shift = -ROW;
            if (drag.from > to && i < drag.from && i >= to) shift = ROW;
          }
          const active = drag && drag.id === id;
          return (
            <div key={id} style={{
              height: ROW - 6, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10, padding: '0 6px 0 12px',
              borderRadius: LL.r.md, background: active ? LL.surface3 : LL.surface2, border: `1px solid ${active ? LL.accent : LL.line}`,
              transform: `translateY(${active ? drag.dy : shift}px)`, transition: active ? 'none' : `transform .18s ${LL.ease}`,
              position: 'relative', zIndex: active ? 2 : 1, boxShadow: active ? LL.shadow.md : 'none',
            }}>
              <span style={{ width: 22, fontFamily: LL.display, fontSize: LL.fs.body, fontWeight: 700, color: LL.fgDim, textAlign: 'center' }}>{i + 1}</span>
              <span style={{ flex: 1, minWidth: 0, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sl.variant?.name || sl.name}</span>
              <button aria-label={`Move ${sl.name} up`} disabled={i === 0} onClick={() => move(i, -1)} style={{ ...iconBtn({ width: 34, height: 34, border: 'none', background: 'transparent', color: i === 0 ? LL.fgFaint : LL.fgMute }) }}><LLIcon.chevUp /></button>
              <button aria-label={`Drag ${sl.name}`} onPointerDown={(e) => onDown(e, id)} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
                style={{ ...iconBtn({ width: 40, height: 44, border: 'none', background: 'transparent', color: LL.fgMute, touchAction: 'none', cursor: 'grab' }) }}>
                <LLIcon.grip />
              </button>
            </div>
          );
        })}
      </div>
      <button onClick={() => onApply(order)} style={{
        marginTop: 8, width: '100%', height: 52, borderRadius: LL.r.md, border: 'none', background: LL.accent, color: LL.accentInk,
        fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 700, cursor: 'pointer',
      }}>Done</button>
    </LLSheet>
  );
}

function PlateSheet({ open, weightLb, units, barLb, platesAvail, onClose }) {
  const kg = units === 'kg';
  const [bar, setBar] = React.useState(null);
  const defaultBar = kg ? 20 : 45;
  const barVal = bar ?? (barLb ? (kg ? Math.round(barLb * KG) : barLb) : defaultBar);
  const total = kg ? Math.round(weightLb * KG * 4) / 4 : weightLb;
  const plates = (Array.isArray(platesAvail) && platesAvail.length ? [...platesAvail].sort((a, b) => b - a) : null) || (kg ? [25, 20, 15, 10, 5, 2.5, 1.25] : [45, 35, 25, 10, 5, 2.5]);
  const res = platesFor(total, { bar: barVal, plates });
  const color = (p) => ({ 45: '#3b6fd8', 25: '#d84a3b', 35: '#e0b43a', 10: '#4caf50', 5: '#9e9e9e', 2.5: '#bdbdbd', 20: '#3b6fd8', 15: '#e0b43a', 1.25: '#bdbdbd' }[p] || '#888');
  return (
    <LLSheet open={open} onClose={onClose} title="Plate calculator" subtitle={`${fmtWeight(weightLb, units)} ${unitLabel(units)} total`}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {(kg ? [20, 15, 10] : [45, 35, 25, 15]).map(b => (
          <button key={b} onClick={() => setBar(b)} style={{
            flex: 1, height: 38, borderRadius: LL.r.sm, cursor: 'pointer', fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600,
            border: `1px solid ${barVal === b ? LL.accent : LL.line}`, background: barVal === b ? LL.accentSoft : LL.surface2, color: barVal === b ? LL.accentText : LL.fg,
          }}>{b} {unitLabel(units)} bar</button>
        ))}
      </div>
      {/* barbell drawing */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, gap: 3, background: LL.surface2, borderRadius: LL.r.md }}>
        <div style={{ width: 70, height: 8, background: LL.line2, borderRadius: LL.r.xs }} />
        {res.perSide.map((p, i) => (
          <div key={i} style={{ width: 12, height: 40 + (p / plates[0]) * 60, borderRadius: LL.r.xs, background: color(p) }} />
        ))}
        <div style={{ width: 14, height: 16, background: LL.line2, borderRadius: LL.r.xs }} />
      </div>
      <div style={{ marginTop: 14, fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fg, textAlign: 'center' }}>
        {res.perSide.length ? <>Each side: <b style={{ fontFamily: LL.display, fontSize: LL.fs.h3 }}>{res.perSide.join(' + ')}</b></> : 'Just the bar'}
      </div>
      {Math.abs(res.achievable - total) > 0.01 && (
        <div style={{ marginTop: 6, fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.warn, textAlign: 'center' }}>
          Closest loadable: {res.achievable} {unitLabel(units)}
        </div>
      )}
    </LLSheet>
  );
}

function RestPickSheet({ open, value, onClose, onPick }) {
  const opts = [30, 45, 60, 90, 120, 150, 180, 240, 300];
  return (
    <LLSheet open={open} onClose={onClose} title="Rest time">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {opts.map(v => (
          <button key={v} onClick={() => onPick(v)} style={{
            height: 52, borderRadius: LL.r.sm, cursor: 'pointer', fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700,
            border: `1px solid ${value === v ? LL.accent : LL.line}`, background: value === v ? LL.accentSoft : LL.surface2, color: value === v ? LL.accentText : LL.fg,
          }}>{fmtClock(v)}</button>
        ))}
      </div>
    </LLSheet>
  );
}

function NoteSheet({ open, slot, exercise, onClose, onSave }) {
  const [text, setText] = React.useState('');
  React.useEffect(() => { if (open) setText(exercise?.note || ''); }, [open, exercise?.note]);
  return (
    <LLSheet open={open} onClose={onClose} title="Exercise note" subtitle={slot?.name || ''}>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={4} maxLength={400}
        placeholder="e.g. Seat 4, pins on 6, pause at the bottom"
        style={{ width: '100%', padding: 12, borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: LL.surface2, color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.body, outline: 'none' }} />
      <button onClick={() => onSave(text.trim())} style={{
        marginTop: 10, width: '100%', height: 50, borderRadius: LL.r.md, border: 'none', background: LL.accent, color: LL.accentInk,
        fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, cursor: 'pointer',
      }}>Save note</button>
    </LLSheet>
  );
}

function FinishSheet({ open, session, slots, elapsed, volume, units, changed, dayName, onClose, onFinish, checkin }) {
  const [save, setSave] = React.useState(false);
  const [mins, setMins] = React.useState('60');
  const total = slots.reduce((a, s) => a + s.rows.length, 0);
  const left = total - session.sets.length;
  // Same counts as the summary: working sets, warm-ups on the side.
  const warm = session.sets.filter(s => s.type === 'warmup').length;
  // Hard sets exactly as the summary counts them (a myo mini-set is half, cardio none).
  const work = Math.round(session.sets.reduce((a, s) => a + ((s.dist > 0 || s.dur > 0) && !(s.reps > 0) ? 0 : hardSetValue(s)), 0) * 2) / 2;
  const stat = (label, value, sub) => (
    <div style={{ flex: 1, minWidth: 0, padding: '10px 12px', background: LL.surface2, borderRadius: LL.r.md }}>
      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, color: LL.fgDim, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      <div style={{ fontFamily: LL.display, fontSize: LL.fs.stat, fontWeight: 700, color: LL.fg, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 2, whiteSpace: 'nowrap' }}>{sub}</div>}
    </div>
  );
  return (
    <LLSheet open={open} onClose={onClose} title="Finish workout?" maxHeight="92%">
      {/* Toasts stay out of the way while this sheet is up (the summary celebrates PRs). */}
      {open && <span data-ll-quiet-toasts="finish" hidden />}
      <div style={{ display: 'flex', gap: 8 }}>
        {session.retro ? (
          <label style={{ flex: 1, padding: '10px 12px', background: LL.surface2, borderRadius: LL.r.md, display: 'block' }}>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, color: LL.fgDim, textTransform: 'uppercase' }}>Minutes</div>
            <input value={mins} inputMode="numeric" aria-label="Workout duration in minutes" onChange={e => setMins(e.target.value.replace(/\D/g, '').slice(0, 3))}
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 2, padding: 0, border: 'none', borderBottom: `1.5px dashed ${LL.accent}`, outline: 'none', background: 'transparent', color: LL.fg, fontFamily: LL.display, fontSize: LL.fs.stat, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} />
          </label>
        ) : stat('Time', fmtClock(elapsed))}
        {stat('Sets', work, warm ? `+ ${warm} warm-up${warm === 1 ? '' : 's'}` : 'working')}
        {stat('Volume', `${fmtK(volume, units)} ${unitLabel(units)}`)}
      </div>
      {left > 0 && (
        <div style={{ marginTop: 12, fontFamily: LL.ui, fontSize: LL.fs.meta, color: ink(LL.warn) }}>
          {left} planned set{left === 1 ? '' : 's'} not logged. They'll be skipped.
        </div>
      )}
      {checkin}
      {changed && (
        <button onClick={() => setSave(v => !v)} style={{
          marginTop: 12, width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: LL.r.md, cursor: 'pointer', textAlign: 'left',
          background: LL.surface2, border: `1px solid ${save ? LL.accent : LL.line}`,
        }}>
          <span style={{ width: 22, height: 22, borderRadius: LL.r.sm, border: `1.5px solid ${save ? LL.accent : LL.line2}`, background: save ? LL.accent : 'transparent', color: LL.accentInk, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {save && <LLIcon.check style={{ width: 16, height: 16 }} />}
          </span>
          <span style={{ flex: 1 }}>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>Update {dayName} in your program</div>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgDim }}>Keep today's exercise and set changes next time</div>
          </span>
        </button>
      )}
      <button onClick={() => onFinish(save, parseInt(mins, 10) || 60)} disabled={session.sets.length === 0} style={{
        marginTop: 14, width: '100%', height: 54, borderRadius: LL.r.md, border: 'none',
        background: session.sets.length ? LL.accent : LL.surface3, color: session.sets.length ? LL.accentInk : LL.fgDim,
        fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 700, cursor: session.sets.length ? 'pointer' : 'default',
      }}>{session.sets.length ? 'Finish & see summary' : 'Log at least one set to finish'}</button>
      <button onClick={onClose} style={{
        marginTop: 8, width: '100%', height: 48, borderRadius: LL.r.md, border: `1px solid ${LL.line}`, background: 'transparent',
        color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer',
      }}>Keep going</button>
    </LLSheet>
  );
}

function ConflictScreen({ live, stale, onResume, onSaveStale, onDiscard, onCancel }) {
  const when = new Date(live.startedAt);
  const dateLabel = when.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  return (
    <div style={{ flex: 1, background: LL.bg, display: 'flex', flexDirection: 'column', padding: 'calc(env(safe-area-inset-top, 0px) + 64px) 22px 22px' }}>
      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1.2, color: stale ? LL.warn : LL.fgDim, textTransform: 'uppercase' }}>{stale ? 'Unfinished workout' : 'Workout in progress'}</div>
      <div style={{ fontFamily: LL.display, fontSize: LL.fs.h2, fontWeight: 800, color: LL.fg, marginTop: 8, lineHeight: 1.1 }}>
        {stale ? `${live.name || 'A workout'} from ${dateLabel} is still open` : `You're mid-way through ${live.name || 'a workout'}`}
      </div>
      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fgMute, marginTop: 10, lineHeight: 1.45 }}>
        {live.sets.length} set{live.sets.length === 1 ? '' : 's'} logged. {stale ? 'Save it to your history (it ends at its last set) or discard it, then start today fresh.' : 'Resume it, or discard it to start a new one.'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24 }}>
        {stale ? (
          <>
            {live.sets.length > 0 && <button onClick={onSaveStale} style={{ height: 54, borderRadius: LL.r.md, border: 'none', background: LL.accent, color: LL.accentInk, fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 700, cursor: 'pointer' }}>Save it and continue</button>}
            <button onClick={onDiscard} style={{ height: 50, borderRadius: LL.r.md, border: `1px solid ${LL.line}`, background: LL.surface, color: LL.danger, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer' }}>Discard it</button>
            <button onClick={onResume} style={{ height: 48, borderRadius: LL.r.md, border: `1px solid ${LL.line}`, background: 'transparent', color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer' }}>Keep logging into it</button>
          </>
        ) : (
          <>
            <button onClick={onResume} style={{ height: 54, borderRadius: LL.r.md, border: 'none', background: LL.accent, color: LL.accentInk, fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 700, cursor: 'pointer' }}>Resume {live.name || 'workout'}</button>
            <button onClick={onDiscard} style={{ height: 50, borderRadius: LL.r.md, border: `1px solid ${LL.line}`, background: LL.surface, color: LL.danger, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer' }}>Discard it and start new</button>
          </>
        )}
        <button onClick={onCancel} style={{ height: 44, border: 'none', background: 'transparent', color: LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.meta, cursor: 'pointer' }}>Cancel</button>
      </div>
    </div>
  );
}

function MicIcon(p) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}>
      <rect x="7" y="2.5" width="6" height="10" rx="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M4.5 9.5a5.5 5.5 0 0011 0M10 15v2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// ——— Quick log: name matching ————————————————————————————————————————
// Spoken / typed names ("run", "ez bar curl", "pull ups") against exercise
// names, with a few everyday synonyms. 1 = exact, ≥ 0.6 = a confident match.
const QL_ALIAS = {
  run: ['running', 'jogging', 'jog', 'treadmill'], jog: ['jogging', 'running'], bike: ['bicycling', 'cycling', 'bike', 'recumbent'],
  cycle: ['cycling', 'bicycling', 'bike'], row: ['rowing', 'rower', 'row'], walk: ['walking', 'treadmill'], swim: ['swimming'],
  ez: ['ez-bar', 'ez'], db: ['dumbbell'], bb: ['barbell'],
  ohp: ['overhead', 'press'], rdl: ['romanian', 'deadlift'], bench: ['bench'], stairs: ['stairmaster', 'step', 'stair'],
};
// "pull-up", "pull ups", "Pullups" → one token, so "pull ups" never matches "Face Pull".
const qlNorm = (s) => String(s || '').toLowerCase().replace(/[(),/]/g, ' ')
  .replace(/\b(pull|push|chin|sit|step|muscle)[\s-]?ups?\b/g, '$1up')
  .replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
const qlTokens = (s) => qlNorm(s).split(' ').filter(t => t && !['the', 'a', 'of', 'with', 'on', 'and'].includes(t));
function qlScore(query, name) {
  const q = qlNorm(query), n = qlNorm(name);
  if (!q || !n) return 0;
  if (q === n) return 1;
  if (q.replace(/[\s]/g, '').replace(/s$/, '') === n.replace(/[\s]/g, '').replace(/s$/, '')) return 1;
  const nt = new Set(qlTokens(name).flatMap(t => [t, t.replace(/s$/, '')]));
  const qt = qlTokens(query);
  if (!qt.length) return 0;
  let hit = 0;
  for (const t of qt) {
    const alts = [t, t.replace(/s$/, ''), t + 's', ...(QL_ALIAS[t] || [])];
    if (alts.some(a => nt.has(a) || nt.has(a.replace(/s$/, '')))) hit++;
  }
  const cover = hit / qt.length;
  const precision = hit / Math.max(1, nt.size / 2);
  return 0.78 * cover + 0.22 * Math.min(1, precision) - (n.includes(q) ? 0 : 0.02);
}

// Log sets by typing or speaking ("185 for 8, 8, 7", "bench 3x5 at 225 rpe 8",
// "5k run 25:30", "plank 60s", "pull ups bw+25 x 8"). A named exercise is
// matched against the workout first, then the whole library; sets are never
// filed under a different exercise than the one named.
function QuickLogSheet({ open, slots, focusSlotId, units, kindFor, effort, onClose, onLog, onAddAndLog, onPickOther }) {
  const [text, setText] = React.useState('');
  const [target, setTarget] = React.useState(null);     // slot id chosen by tap
  const [libPick, setLibPick] = React.useState(null);   // library exercise chosen by tap
  const [listening, setListening] = React.useState(false);
  const [ai, setAi] = React.useState({ busy: false, parsed: null, err: null, for: '' });
  const recRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const { items } = useExerciseIndex();
  const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

  React.useEffect(() => {
    if (open) {
      setText(''); setTarget(null); setLibPick(null); setAi({ busy: false, parsed: null, err: null, for: '' });
      const t = setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 260); // after the sheet settles
      return () => clearTimeout(t);
    }
    try { recRef.current?.stop(); } catch {} setListening(false);
  }, [open]);

  const local = text.trim() ? parseQuickLog(text, { units }) : null;
  const parsed = local || (ai.for === text && ai.parsed) || null;
  // Free-form fallback: Claude turns anything ("did three of eight on the hack squat with two plates") into sets.
  const interpret = async () => {
    setAi({ busy: true, parsed: null, err: null, for: text });
    try {
      const out = await completeJSON({
        system: 'Extract strength-training sets from what a lifter typed or said mid-workout. One entry per set (expand "3x8" into three sets). weight is the load number as stated (0 for bodyweight, -1 if not stated); unit is "kg" or "lb" (default ' + units + '); rpe is 0 when not stated; exercise is the exercise named, or "" if none.',
        prompt: text,
        maxTokens: 1200,
        schema: {
          type: 'object', additionalProperties: false, required: ['exercise', 'unit', 'sets'],
          properties: {
            exercise: { type: 'string' },
            unit: { type: 'string', enum: ['kg', 'lb'] },
            sets: { type: 'array', items: { type: 'object', additionalProperties: false, required: ['weight', 'reps', 'rpe'], properties: { weight: { type: 'number' }, reps: { type: 'integer' }, rpe: { type: 'number' } } } },
          },
        },
      });
      const sets = (out.sets || []).filter(x => x.reps > 0 && x.reps <= 100).slice(0, 12)
        .map(x => ({ w: x.weight < 0 ? null : x.weight, reps: x.reps, rpe: x.rpe >= 5 && x.rpe <= 10 ? x.rpe : null }));
      setAi({ busy: false, err: sets.length ? null : 'No sets found in that.', for: text, parsed: sets.length ? { exercise: out.exercise || null, unit: out.unit, units, sets, kind: 'reps' } : null });
    } catch (e) {
      setAi({ busy: false, parsed: null, err: e.message, for: text });
    }
  };

  const pkind = parsed?.kind || 'reps';
  const fits = (k) => (pkind === 'cardio' ? k.track === 'distance-time' : pkind === 'time' ? k.track === 'time' : k.track === 'reps');
  // 1) a slot in this workout, 2) the library (your exercises first), 3) nothing → ask.
  const match = React.useMemo(() => {
    const name = parsed?.exercise;
    if (!name) return null;
    let best = null;
    for (const s of slots) {
      const sc = Math.max(qlScore(name, s.variant?.name || s.name), qlScore(name, s.name)) + (fits(kindFor(s)) ? 0.02 : -0.2);
      if (sc >= 0.6 && (!best || sc > best.score)) best = { slot: s, score: sc };
    }
    if (best) return best;
    const cands = [];
    for (const it of items) {
      if (it.category === 'stretch') continue;
      const k = exerciseKind(it);
      let sc = qlScore(name, it.name) + (it.h ? 0.06 : 0) + (it.src === 'state' ? 0.02 : 0) + (fits(k) ? 0.02 : -0.25);
      if (sc >= 0.6) cands.push({ item: it, score: sc });
    }
    cands.sort((a, b) => b.score - a.score || a.item.name.length - b.item.name.length);
    return cands.length ? { lib: cands.slice(0, 3), ambiguous: cands.length > 1 && cands[0].score - cands[1].score < 0.04 } : { none: true };
  }, [parsed?.exercise, pkind, slots, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const named = !!parsed?.exercise;
  const chosenSlot = target ? slots.find(s => s.id === target) : null;
  const libItem = libPick ? libPick : (match?.lib && !match.ambiguous ? match.lib[0].item : null);
  const slot = chosenSlot || match?.slot || (!named ? slots.find(s => s.id === focusSlotId) : null) || null;
  const destKind = slot ? kindFor(slot) : libItem ? exerciseKind(libItem) : null;
  const mismatch = parsed && destKind && !fits(destKind) && !(pkind === 'reps' && destKind.track === 'time');
  const sets = parsed ? parsed.sets.map(s => ({ ...s, w: toLb(s.w, parsed, s) })) : [];
  const needWeight = libItem && !slot && pkind === 'reps' && sets.some(s => s.w == null) && !isBwLoad(exerciseKind(libItem).load);
  const canLog = !!parsed && sets.length && !mismatch && (slot || (libItem && !needWeight));
  const doLog = () => {
    if (!canLog) return;
    if (slot) onLog(slot, sets);
    else onAddAndLog(itemToExercise(libItem), sets);
  };

  const listen = () => {
    if (!SR) return;
    if (listening) { try { recRef.current?.stop(); } catch {} return; }
    const rec = new SR();
    rec.lang = navigator.language || 'en-US';
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => setText(Array.from(e.results).map(r => r[0].transcript).join(' '));
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    try { rec.start(); } catch { setListening(false); }
  };

  const chip = (k, label, on, onClick) => (
    <button key={k} onClick={onClick} aria-pressed={on} style={{
      flexShrink: 0, height: 34, maxWidth: 240, padding: '0 12px', borderRadius: LL.r.pill, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      border: `1px solid ${on ? LL.accent : LL.line2}`, background: on ? LL.accentSoft : 'transparent', color: on ? LL.accentText : LL.fgMute,
      fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600,
    }}>{label}</button>
  );
  const kindOfDest = destKind || { load: parsed?.load || 'weighted', track: pkind === 'cardio' ? 'distance-time' : pkind === 'time' ? 'time' : 'reps' };
  const preview = (s) => {
    if (kindOfDest.track === 'distance-time') return fmtSetText({ ...s, w: 0 }, kindOfDest, units);
    if (s.w == null) return `${kindOfDest.track === 'time' ? fmtDuration(s.reps) : `auto × ${s.reps}`}`;
    return fmtSetText(s, kindOfDest, units, { effort });
  };
  const logLabel = (() => {
    const n = sets.length; const what = pkind === 'cardio' ? 'interval' : 'set';
    const cnt = `${n} ${what}${n > 1 ? 's' : ''}`;
    if (!slot && libItem) return pkind === 'cardio' && n === 1 ? `Add ${libItem.name} · log it` : `Add ${libItem.name} · log ${cnt}`;
    return n ? (pkind === 'cardio' && n === 1 ? 'Log it' : `Log ${cnt}`) : 'Log sets';
  })();

  return (
    <LLSheet open={open} onClose={onClose} title="Quick log" subtitle="Type or say your sets" maxHeight="88%">
      <div style={{ display: 'flex', gap: 8 }}>
        <input ref={inputRef} value={text} onChange={e => { setText(e.target.value); setLibPick(null); }}
          onKeyDown={e => { if (e.key === 'Enter') doLog(); }}
          placeholder='"185 for 8, 8, 7" · "5k run 25:30"' aria-label="Sets to log"
          style={{ flex: 1, minWidth: 0, height: 52, padding: '0 14px', borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: LL.surface2, color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.btn, outline: 'none' }} />
        {SR && (
          <button aria-label={listening ? 'Stop listening' : 'Speak'} onClick={listen} style={{
            width: 52, height: 52, borderRadius: LL.r.md, border: 'none', cursor: 'pointer', flexShrink: 0,
            background: listening ? LL.danger : LL.accent, color: listening ? '#fff' : LL.accentInk,
            display: 'flex', alignItems: 'center', justifyContent: 'center', animation: listening ? 'llPulse 1s infinite' : undefined,
          }}><MicIcon style={{ width: 22, height: 22 }} /></button>
        )}
      </div>
      {slots.length > 0 && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', margin: '12px 0 4px', paddingBottom: 4 }}>
          {slots.map(s => chip(s.id, s.variant?.name || s.name, slot?.id === s.id, () => { setTarget(s.id); setLibPick(null); }))}
        </div>
      )}
      <div style={{ minHeight: 64, marginTop: 8, padding: 14, borderRadius: LL.r.md, background: LL.surface2, border: `1px solid ${LL.line}` }}>
        {!text.trim() ? (
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgDim, lineHeight: 1.5 }}>
            Works with "185x8", "185 for 8 8 7", "3 sets of 10 at 60", "bench 225 for 5 at rpe 8", "pull ups bw+25 x 8", "5k run 25:30", "plank 60s". Say the exercise first to log to a different one.
          </div>
        ) : !parsed ? (
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: ink(LL.warn) }}>
            {ai.busy ? <span style={{ color: LL.fgMute }}>Interpreting…</span> : ai.err ? ai.err : 'Couldn\'t read that. Try "weight x reps".'}
            {aiEnabled() && !ai.busy && (
              <button onClick={interpret} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, padding: 0, background: 'none', border: 'none', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, cursor: 'pointer' }}>
                <LLIcon.ai style={{ width: 14, height: 14 }} /> Interpret with AI
              </button>
            )}
          </div>
        ) : (
          <div>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute }}>
              {slot ? (slot.variant?.name || slot.name)
                : libItem ? <><b style={{ color: LL.fg }}>{libItem.name}</b> isn't in this workout yet</>
                : named ? <>No exercise called <b style={{ color: LL.fg }}>"{parsed.exercise}"</b>. Pick one</>
                : 'Pick an exercise'}
            </div>
            {!slot && match?.lib && match.ambiguous && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {match.lib.map(c => chip(c.item.id, c.item.name, libPick?.id === c.item.id, () => setLibPick(c.item)))}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {sets.map((s, i) => (
                <span key={i} style={{ padding: '6px 10px', borderRadius: LL.r.sm, background: LL.accentSoft, color: LL.accentText, fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {preview(s)}
                </span>
              ))}
            </div>
            {mismatch && (
              <div style={{ marginTop: 8, fontFamily: LL.ui, fontSize: LL.fs.meta, color: ink(LL.warn) }}>
                {pkind === 'cardio' ? 'That reads as distance / time. Pick a cardio exercise.' : pkind === 'time' ? 'That reads as a timed hold. Pick a timed exercise.' : 'That reads as weight × reps. Pick a lifting exercise.'}
              </div>
            )}
            {needWeight && (
              <div style={{ marginTop: 8, fontFamily: LL.ui, fontSize: LL.fs.meta, color: ink(LL.warn) }}>Add the weight too, e.g. "{parsed.exercise} 60x12".</div>
            )}
            {parsed.unparsed?.length > 0 && (
              <div style={{ marginTop: 8, fontFamily: LL.ui, fontSize: LL.fs.meta, color: ink(LL.warn) }}>
                Not logged. Couldn't read: "{parsed.unparsed.join('", "')}"
              </div>
            )}
            {(named || !slot) && (
              <button onClick={() => onPickOther(sets)} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, padding: 0, background: 'none', border: 'none', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, cursor: 'pointer' }}>
                <LLIcon.search style={{ width: 14, height: 14 }} /> {slot || libItem ? 'A different exercise…' : 'Find it in the library…'}
              </button>
            )}
          </div>
        )}
      </div>
      <button disabled={!canLog} onClick={doLog} style={{
        marginTop: 14, width: '100%', height: 54, borderRadius: LL.r.md, border: 'none',
        background: canLog ? LL.accent : LL.surface3, color: canLog ? LL.accentInk : LL.fgDim,
        fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 700, cursor: canLog ? 'pointer' : 'default',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0 16px',
      }}>{logLabel}</button>
    </LLSheet>
  );
}

// ——— App-wide: minimized workout bar + rest completion watcher ————————————

export function WorkoutMiniBar() {
  const { state, route, setRoute } = useApp();
  const [now, setNow] = React.useState(Date.now());
  const session = state.sessions.find(s => s.id === state.activeSessionId && !s.completedAt);
  React.useEffect(() => {
    if (!session) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [session?.id]);
  // Home has its own card for a live workout (Resume) and for a stale one (Save it / Discard).
  const shown = !!session && route.name !== 'active-workout' && route.name !== 'welcome' && route.name !== 'home';
  const stale = !!session && isStaleSession(session);
  // Screens reserve this much room at the bottom (shell's router reads the var)
  // so the bar never covers their last row, FAB or destructive action.
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    if (shown) root.style.setProperty('--ll-mini-h', '68px'); else root.style.removeProperty('--ll-mini-h');
    return () => root.style.removeProperty('--ll-mini-h');
  }, [shown]);
  if (!shown) return null;
  // Sits above the tab bar when there is one, else docks at the bottom edge.
  const tabs = !['program-edit', 'exercise-edit', 'settings/', 'workout-summary', 'workout-review', 'workout-preview', 'ai-coach'].some(p => route.name.startsWith(p));
  const resting = session.rest && session.rest.endsAt > now;
  return (
    <button onClick={() => setRoute('active-workout', { dayId: session.dayId || undefined })} style={{
      // Desktop: beside the side rail (--ll-rail-w), centered, docked low (no bottom tab bar).
      position: 'absolute', left: 'calc(var(--ll-rail-w, 0px) + 10px)', right: 10, maxWidth: 700, marginInline: 'auto',
      bottom: `calc(env(safe-area-inset-bottom, 0px) + ${tabs ? 'var(--ll-bottom-nav, 78px)' : '10px'})`, zIndex: 60,
      height: 56, borderRadius: LL.r.md, border: `1px solid color-mix(in oklab, ${LL.accent} 45%, ${LL.line})`,
      background: LL.surface2, boxShadow: LL.shadow.lift, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 12, padding: '0 14px', textAlign: 'left',
    }}>
      <span style={{ width: 10, height: 10, borderRadius: LL.r.pill, background: LL.accent, animation: 'llPulse 1.6s infinite', flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, color: LL.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{session.name || 'Workout'}</div>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, fontVariantNumeric: 'tabular-nums' }}>
          {stale ? `Unfinished · ${new Date(session.startedAt).toLocaleDateString(undefined, { weekday: 'short' })}` : fmtClock((now - session.startedAt) / 1000)} · {session.sets.length} set{session.sets.length === 1 ? "" : "s"}{resting && !stale ? ` · rest ${fmtClock((session.rest.endsAt - now) / 1000)}` : ''}
        </div>
      </span>
      <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, color: LL.accentText }}>Resume</span>
    </button>
  );
}

export function RestWatcher() {
  const { state, actions } = useApp();
  const session = state.sessions.find(s => s.id === state.activeSessionId && !s.completedAt);
  const rest = session?.rest;
  // Lock-screen rest alert (Web Push, when enabled in Sync & backup): schedule it
  // whenever a rest starts or its end moves; cancel on skip, in-app completion,
  // finish or discard. Both calls are no-ops when push is off.
  const pushed = React.useRef(false);
  React.useEffect(() => {
    const live = rest && !rest.fired && rest.endsAt > Date.now();
    if (live) {
      pushed.current = true;
      pushRestAlert({ at: rest.endsAt, title: 'Rest over', body: rest.label || 'Time for your next set' }).catch?.(() => {});
    } else if (pushed.current) {
      pushed.current = false;
      cancelRestAlert().catch?.(() => {});
    }
  }, [rest?.endsAt, rest?.fired, session?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!rest || rest.fired) return;
    const ms = rest.endsAt - Date.now();
    const fire = () => {
      // Two open tabs each run this watcher: only the first one alerts.
      let first = true;
      try {
        if (localStorage.getItem('liftlog.restAlerted') === String(rest.endsAt)) first = false;
        else localStorage.setItem('liftlog.restAlerted', String(rest.endsAt));
      } catch {}
      // Relaunching long after a rest ended: just mark it done, no beep.
      const late = Date.now() - rest.endsAt > 60 * 1000;
      if (!first || late) { actions.updateSession(session.id, { rest: { ...rest, fired: true } }); return; }
      try { playRestBeep({ sound: state.prefs.sound }); } catch {}
      if (state.prefs.haptics) { try { tryVibrate([80, 60, 80]); } catch {} }
      if (state.prefs.notifRest) { try { fireRestNotification('Rest over', 'Time for your next set'); } catch {} }
      try { fireRestFlash(); } catch {}
      actions.updateSession(session.id, { rest: { ...rest, fired: true } });
    };
    if (ms <= 0) { fire(); return; }
    const t = setTimeout(fire, ms);
    const onVis = () => { if (!document.hidden && Date.now() >= rest.endsAt) fire(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearTimeout(t); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest?.endsAt, rest?.fired, session?.id]);
  return null;
}
